import React, { useState, useEffect, useCallback, useRef, createContext, useContext } from "react";
import axios from "axios";
import { BrowserRouter, Routes, Route, NavLink, useNavigate, Navigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Settings, Plus, Trash2, Wrench,
  Play, Loader2, CheckCircle2, XCircle, X, LogOut,
  RefreshCw, Wifi, WifiOff, ChevronRight, BookOpen,
  Zap, Clock, AlertTriangle, Target, Lock, Shield, Monitor, Globe, KeyRound, Trophy, TrendingUp, TrendingDown, Users, Mail, CalendarClock, Send, CandlestickChart, Activity, ShieldAlert, BarChart3, Layers, Copy, Check, Download, Menu, ExternalLink
} from "lucide-react";
import "@/App.css";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend, ResponsiveContainer } from "recharts";
import MarketForge from "@/MarketForge";
import PageErrorBoundary from "@/components/PageErrorBoundary";
import FreshnessIndicator from "@/components/FreshnessIndicator";
import InlineError from "@/components/InlineError";
import { I18nProvider, useI18n, LanguageToggle } from "@/i18n";
import Dashboard from "@/pages/Dashboard";
import DeveloperTools from "@/pages/DeveloperTools";
// Iter111 — V2 redesigned Strategy Scanner (trading-card auto-scan).
// Legacy UnifiedStrategyScanner.js was deleted in Iter111 per operator sign-off.
import StrategyScannerV2 from "@/pages/StrategyScannerV2";
// Iter114 — Cycle Tracker overhaul moved to a dedicated page file
// (`/app/frontend/src/pages/CycleTracker.js`) — manual entry,
// rolls, cost-basis graph, recovery panel, live-spot pull.
import CycleTrackerPage from "@/pages/CycleTracker";
import { MarketSnapshotProvider } from "@/contexts/MarketSnapshotContext";
import { CFMInventoryProvider } from "@/contexts/CFMInventoryContext";
import { WESContextProvider } from "@/contexts/WESContextProvider";
// Iter109 — Market Scanner → Strategy Scanner bridge.
import { MarketScannerBridgeProvider } from "@/contexts/MarketScannerBridgeContext";
import SettingsPage from "@/pages/SettingsPage";
import MarketScannerPage from "@/pages/MarketScanner";
import Leaderboard from "@/pages/Leaderboard";
import Performance from "@/pages/Performance";
import ShadowMode from "@/pages/ShadowMode";
import WesEngineBadge from "@/components/WesEngineBadge";
import { Toaster } from "@/components/ui/sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export { API };

// Configure axios to send cookies
axios.defaults.withCredentials = true;

// ==================== AUTH CONTEXT ====================
const AuthContext = createContext(null);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsSetup, setNeedsSetup] = useState(false);

  useEffect(() => {
    bootstrap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const bootstrap = async () => {
    // If returning from Google OAuth callback, AuthCallback will handle the session exchange.
    if (window.location.hash && window.location.hash.includes("session_id=")) {
      setLoading(false);
      return;
    }
    // Check setup state first (cheap, public endpoint)
    try {
      const s = await axios.get(`${API}/setup/status`);
      setNeedsSetup(!!s.data?.needs_setup);
    } catch {
      setNeedsSetup(false);
    }
    // Then attempt to load current user from JWT cookie
    try {
      const resp = await axios.get(`${API}/auth/me`);
      setUser(resp.data);
    } catch {
      setUser(null);
    }
    setLoading(false);
  };

  const login = async (email, password) => {
    const resp = await axios.post(`${API}/auth/login`, { email, password });
    setUser(resp.data);
    return resp.data;
  };

  const completeSetup = async (email, password, name) => {
    const resp = await axios.post(`${API}/setup/admin`, { email, password, name });
    setNeedsSetup(false);
    setUser(resp.data);
    return resp.data;
  };

  const finishGoogleLogin = async (sessionId) => {
    const resp = await axios.post(
      `${API}/auth/google/session`,
      {},
      { headers: { "X-Session-ID": sessionId } }
    );
    setUser(resp.data);
    return resp.data;
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch {}
    setUser(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#08080A]">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#00FF88] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-xs text-[#8A8A93] font-mono uppercase tracking-widest">Initializing</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, needsSetup, login, logout, completeSetup, finishGoogleLogin, refresh: bootstrap }}>
      {children}
    </AuthContext.Provider>
  );
}

function useAuth() { return useContext(AuthContext); }
export { useAuth, AuthContext };

// ==================== SETUP WIZARD (FIRST RUN) ====================
function SetupAdminPage() {
  const { completeSetup } = useAuth();
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError("Password must be at least 8 characters"); return; }
    if (password !== confirmPassword) { setError("Passwords do not match"); return; }
    setLoading(true);
    try {
      await completeSetup(email, password, name || "Admin");
    } catch (err) {
      setError(err.response?.data?.detail || "Setup failed");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#040604] flex items-center justify-center relative overflow-hidden">
      <div className="absolute inset-0" style={{
        backgroundImage: `url(https://customer-assets.emergentagent.com/job_ai-premium-tool/artifacts/pb70nfk9_gemini-2.5-flash-image_a_surreal_and_vibrant_cinematic_photo_of_Create_an_image_for_a_web_application_d-0.jpg)`,
        backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.30,
      }} />
      <div className="absolute inset-0 bg-gradient-to-t from-[#040604] via-[#040604]/70 to-transparent" />
      <div className="relative z-10 w-[440px]">
        <div className="flex justify-end mb-3">
          <LanguageToggle testid="setup-language-toggle" />
        </div>
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center mb-4">
            <div className="w-12 h-12 border border-[#00FF88]/30 rounded-sm flex items-center justify-center bg-[#00FF88]/5 backdrop-blur-sm">
              <KeyRound size={24} className="text-[#00FF88]" />
            </div>
          </div>
          <h1 className="text-3xl font-black tracking-[-0.03em] text-white font-['Chivo']">{t("setup.first_run")}</h1>
          <p className="text-sm font-bold tracking-[0.2em] text-[#00FF88] mt-1 uppercase">{t("setup.create_admin")}</p>
          <p className="text-[11px] text-[#8A8A93] mt-3 font-mono">
            {t("setup.note")}
          </p>
        </div>
        <form onSubmit={handleSubmit} data-testid="setup-form" className="bg-[#121216] border border-[#26262B] rounded-sm p-6">
          <div className="mb-4">
            <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8A8A93] mb-2 block">{t("setup.display_name")}</label>
            <input data-testid="setup-name" type="text" value={name} onChange={e => setName(e.target.value)}
              className="w-full bg-[#08080A] border border-[#26262B] focus:border-[#00FF88]/50 focus:outline-none text-white rounded-sm px-4 py-3 text-sm font-mono"
              placeholder="Admin" />
          </div>
          <div className="mb-4">
            <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8A8A93] mb-2 block">{t("setup.admin_email")}</label>
            <input data-testid="setup-email" type="email" required value={email} onChange={e => setEmail(e.target.value)}
              className="w-full bg-[#08080A] border border-[#26262B] focus:border-[#00FF88]/50 focus:outline-none text-white rounded-sm px-4 py-3 text-sm font-mono"
              placeholder="you@arxmentis.ltd" autoFocus />
          </div>
          <div className="mb-4">
            <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8A8A93] mb-2 block">{t("setup.password")}</label>
            <input data-testid="setup-password" type="password" required value={password} onChange={e => setPassword(e.target.value)}
              className="w-full bg-[#08080A] border border-[#26262B] focus:border-[#00FF88]/50 focus:outline-none text-white rounded-sm px-4 py-3 text-sm font-mono" />
          </div>
          <div className="mb-5">
            <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8A8A93] mb-2 block">{t("setup.confirm_password")}</label>
            <input data-testid="setup-password-confirm" type="password" required value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
              className="w-full bg-[#08080A] border border-[#26262B] focus:border-[#00FF88]/50 focus:outline-none text-white rounded-sm px-4 py-3 text-sm font-mono" />
          </div>
          {error && (
            <div className="mb-4 py-2 px-3 border border-[#FF3366]/20 bg-[#FF3366]/5 rounded-sm flex items-center gap-2">
              <Lock size={12} className="text-[#FF3366]" />
              <span className="text-xs text-[#FF3366] font-mono">{error}</span>
            </div>
          )}
          <button data-testid="setup-submit" type="submit" disabled={loading || !email || !password || !confirmPassword}
            className="w-full bg-[#00FF88] text-[#08080A] font-bold py-3 rounded-sm hover:bg-[#33FF9F] transition-all text-sm tracking-wide disabled:opacity-30 disabled:cursor-not-allowed">
            {loading ? <Loader2 size={16} className="animate-spin mx-auto" /> : t("setup.submit")}
          </button>
        </form>
      </div>
    </div>
  );
}

// ==================== AUTH CALLBACK (Emergent Google OAuth) ====================
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AuthCallback() {
  const { finishGoogleLogin } = useAuth();
  const [error, setError] = useState("");
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;
    if (!sessionId) {
      setError("Missing session_id");
      return;
    }
    (async () => {
      try {
        await finishGoogleLogin(sessionId);
        // Clean fragment and go to dashboard
        window.history.replaceState(null, "", "/");
        window.location.replace("/");
      } catch (err) {
        setError(err.response?.data?.detail || "Google sign-in failed");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex items-center justify-center min-h-screen bg-[#08080A]">
      <div className="text-center max-w-md px-6">
        {error ? (
          <>
            <div className="w-12 h-12 mx-auto mb-4 border border-[#FF3366]/30 rounded-sm flex items-center justify-center bg-[#FF3366]/5">
              <XCircle size={24} className="text-[#FF3366]" />
            </div>
            <p className="text-sm text-[#FF3366] font-mono mb-4">{error}</p>
            <a href="/" className="text-xs text-[#00FF88] underline font-mono uppercase tracking-widest">Back to login</a>
          </>
        ) : (
          <>
            <div className="w-8 h-8 border-2 border-[#00FF88] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-xs text-[#8A8A93] font-mono uppercase tracking-widest">Verifying Google session</p>
          </>
        )}
      </div>
    </div>
  );
}

// ==================== LOGIN GATE ====================
function LoginPage() {
  const { login } = useAuth();
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState(0); // animation phases

  useEffect(() => {
    const t1 = setTimeout(() => setPhase(1), 100);
    const t2 = setTimeout(() => setPhase(2), 600);
    const t3 = setTimeout(() => setPhase(3), 1100);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(err.response?.data?.detail || t("auth.access_denied"));
    }
    setLoading(false);
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#040604] flex items-center justify-center relative overflow-hidden">
      {/* Background image — ARX MENTIS fortress */}
      <div className="absolute inset-0" style={{
        backgroundImage: `url(https://customer-assets.emergentagent.com/job_ai-premium-tool/artifacts/pb70nfk9_gemini-2.5-flash-image_a_surreal_and_vibrant_cinematic_photo_of_Create_an_image_for_a_web_application_d-0.jpg)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        opacity: 0.35,
      }} />
      {/* Dark overlay for readability */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#040604] via-[#040604]/70 to-transparent" />

      {/* Login Card */}
      <div className={`relative z-10 w-[400px] transition-all duration-700 ${phase >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        {/* Top-right language toggle */}
        <div className="flex justify-end mb-3">
          <LanguageToggle testid="login-language-toggle" />
        </div>
        {/* Logo */}
        <div className={`text-center mb-10 transition-all duration-500 ${phase >= 2 ? 'opacity-100' : 'opacity-0'}`}>
          <div className="inline-flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-12 border border-[#00FF88]/30 rounded-sm flex items-center justify-center bg-[#00FF88]/5 backdrop-blur-sm">
              <Shield size={24} className="text-[#00FF88]" />
            </div>
          </div>
          <h1 className="text-3xl font-black tracking-[-0.03em] text-white font-['Chivo']">
            {t("auth.title")}
          </h1>
          <p className="text-sm font-bold tracking-[0.2em] text-[#00FF88] mt-1 uppercase">
            {t("auth.subtitle")}
          </p>
          <div className="flex items-center justify-center gap-3 mt-3">
            <div className="h-px w-12 bg-gradient-to-r from-transparent to-[#00FF88]/20" />
            <p className="text-[9px] uppercase tracking-[0.3em] text-[#8A8A93] font-['IBM_Plex_Sans']">
              {t("auth.tagline")}
            </p>
            <div className="h-px w-12 bg-gradient-to-l from-transparent to-[#00FF88]/20" />
          </div>
        </div>

        {/* Form */}
        <div className={`transition-all duration-500 ${phase >= 3 ? 'opacity-100' : 'opacity-0'}`}>
          <form onSubmit={handleSubmit} className="bg-[#121216] border border-[#26262B] rounded-sm p-6">
            {/* Google login button */}
            <button
              type="button"
              onClick={handleGoogle}
              data-testid="login-google"
              className="w-full mb-4 bg-white text-[#08080A] font-semibold py-3 rounded-sm hover:bg-[#F4F4F5] transition-all text-sm tracking-wide flex items-center justify-center gap-3"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
                <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
                <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
                <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
              </svg>
              {t("auth.google")}
            </button>
            <div className="relative my-4 text-center">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[#26262B]"></div></div>
              <span className="relative px-3 text-[10px] text-[#8A8A93] font-mono uppercase tracking-[0.2em] bg-[#121216]">{t("auth.or_password")}</span>
            </div>
            <div className="mb-5">
              <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8A8A93] mb-2 block">
                {t("auth.email")}
              </label>
              <input
                data-testid="login-email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="w-full bg-[#08080A] border border-[#26262B] focus:border-[#00FF88]/50 focus:outline-none text-white rounded-sm px-4 py-3 text-sm font-mono transition-colors"
                placeholder={t("auth.email").toLowerCase()}
              />
            </div>
            <div className="mb-5">
              <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8A8A93] mb-2 block">
                {t("auth.password")}
              </label>
              <input
                data-testid="login-password"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full bg-[#08080A] border border-[#26262B] focus:border-[#00FF88]/50 focus:outline-none text-white rounded-sm px-4 py-3 text-sm font-mono transition-colors"
                placeholder={t("auth.password").toLowerCase()}
              />
            </div>

            {error && (
              <div className="mb-4 py-2 px-3 border border-[#FF3366]/20 bg-[#FF3366]/5 rounded-sm flex items-center gap-2">
                <Lock size={12} className="text-[#FF3366]" />
                <span className="text-xs text-[#FF3366] font-mono">{error}</span>
              </div>
            )}

            <button
              data-testid="login-submit"
              type="submit"
              disabled={loading || !email || !password}
              className="w-full bg-[#00FF88] text-[#08080A] font-bold py-3 rounded-sm hover:bg-[#33FF9F] transition-all text-sm tracking-wide disabled:opacity-30 disabled:cursor-not-allowed"
            >
              {loading ? (
                <Loader2 size={16} className="animate-spin mx-auto" />
              ) : (
                t("auth.enter")
              )}
            </button>
          </form>

          {/* Subtle market quote */}
          <div className="mt-6 text-center">
            <p className="text-[10px] text-[#26262B] font-mono italic">
              forge.arxmentis.ltd
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ==================== PROTECTED ROUTE ====================
function ProtectedRoute({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// ==================== SIDEBAR ====================
// Phase 7E (Iter120) — Mobile-responsive sidebar.  Behaves as a
// fixed 220 px rail on md+ (>=768 px) and as a slide-out drawer
// below that breakpoint.  `mobileOpen` controls the off-canvas
// state; `onClose` is fired on backdrop tap, link click, and
// sign-out so the drawer never blocks the destination view.
function Sidebar({ mobileOpen = false, onClose = () => {} }) {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const isAdmin = user?.role === "admin";
  const links = [
    { path: "/", label: t("nav.dashboard"), icon: LayoutDashboard, id: "nav-dashboard" },
    { path: "/strategy-scanner", label: "Strategy Scanner", icon: Layers, id: "nav-strategy-scanner" },
    { path: "/journal", label: t("nav.journal") || "Decision Journal", icon: Target, id: "nav-journal" },
    { path: "/log", label: t("nav.log"), icon: BookOpen, id: "nav-log" },
    { path: "/market-scanner", label: t("nav.market_scanner"), icon: Globe, id: "nav-market-scanner" },
    { path: "/forge", label: t("nav.forge"), icon: CandlestickChart, id: "nav-forge" },
    { path: "/performance", label: t("nav.performance"), icon: BarChart3, id: "nav-performance" },
    ...(isAdmin ? [{ path: "/leaderboard", label: t("nav.leaderboard"), icon: Trophy, id: "nav-leaderboard" }] : []),
    ...(isAdmin ? [{ path: "/shadow", label: "Shadow Mode", icon: ShieldAlert, id: "nav-shadow" }] : []),
    { path: "/dev/tools", label: "Developer Tools", icon: Wrench, id: "nav-developer-tools" },
    { path: "/settings", label: t("nav.settings"), icon: Settings, id: "nav-settings" },
  ];
  return (
    <>
      {/* Mobile backdrop — dims + dismisses the drawer on tap. */}
      {mobileOpen && (
        <div
          data-testid="sidebar-backdrop"
          onClick={onClose}
          className="md:hidden fixed inset-0 z-30 bg-black/70 backdrop-blur-sm"
        />
      )}
      <aside
        data-testid="sidebar"
        className={`w-[220px] min-h-screen border-r border-[#26262B] bg-[#08080A] flex flex-col fixed left-0 top-0 z-40 transition-transform duration-200 ease-out ${mobileOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0`}
      >
        <div className="p-5 border-b border-[#26262B] flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 border border-[#00FF88]/30 rounded-sm flex items-center justify-center bg-[#00FF88]/5">
              <Shield size={16} className="text-[#00FF88]" />
            </div>
            <div>
              <h1 className="text-[13px] font-black tracking-tight text-white font-['Chivo'] leading-none">ARX MENTIS</h1>
              <p className="text-[9px] uppercase tracking-[0.15em] text-[#00FF88] font-bold mt-0.5">Income Engine</p>
            </div>
          </div>
          {/* Mobile-only close button so the operator can dismiss the
              drawer without tapping the backdrop. Hidden on md+. */}
          <button
            type="button"
            data-testid="sidebar-mobile-close"
            onClick={onClose}
            aria-label="Close menu"
            className="md:hidden text-[#8A8A93] hover:text-white p-1 -mr-1"
          >
            <X size={18} />
          </button>
        </div>
        <nav className="flex-1 p-3 flex flex-col gap-1 overflow-y-auto">
          {links.map(l => (
            <NavLink key={l.path} to={l.path} end={l.path === "/"} data-testid={l.id}
              onClick={onClose}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-colors rounded-sm ${isActive ? "bg-[#00FF88]/10 text-[#00FF88] border border-[#00FF88]/20" : "text-[#8A8A93] hover:text-white hover:bg-[#1A1A20] border border-transparent"}`}>
              <l.icon size={18} />{l.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-[#26262B] space-y-2">
          <LanguageToggle className="w-full justify-center flex" testid="sidebar-language-toggle" />
          {user && (
            <div data-testid="sidebar-user-info" className="flex items-center justify-between gap-2 px-1">
              <div className="min-w-0 flex-1">
                <p className="text-[11px] text-white font-mono truncate" title={user.email}>{user.name || user.email}</p>
                <span data-testid="role-badge" className={`inline-block mt-0.5 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-[0.15em] rounded-sm ${isAdmin ? "bg-[#00FF88]/10 text-[#00FF88] border border-[#00FF88]/20" : "bg-[#FFB800]/10 text-[#FFB800] border border-[#FFB800]/20"}`}>
                  {isAdmin ? t("role.admin") : t("role.viewer")}
                </span>
              </div>
            </div>
          )}
          <button onClick={() => { onClose(); logout(); }} data-testid="logout-button" className="flex items-center gap-2 text-xs text-[#8A8A93] hover:text-[#FF3366] transition-colors w-full">
            <LogOut size={14} /> {t("action.signout")}
          </button>
        </div>
      </aside>
    </>
  );
}

// Mobile-only top bar.  Provides the hamburger toggle + ARX MENTIS
// brand mark on <md viewports where the sidebar lives off-canvas.
function MobileTopBar({ onOpen }) {
  return (
    <div
      data-testid="mobile-topbar"
      className="md:hidden sticky top-0 z-30 flex items-center gap-3 px-4 py-3 bg-[#08080A]/95 backdrop-blur-sm border-b border-[#26262B]"
    >
      <button
        type="button"
        data-testid="mobile-menu-toggle"
        onClick={onOpen}
        aria-label="Open menu"
        className="p-1.5 -ml-1.5 rounded-sm text-[#E0E0E0] hover:bg-[#1A1A20]"
      >
        <Menu size={20} />
      </button>
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 border border-[#00FF88]/30 rounded-sm flex items-center justify-center bg-[#00FF88]/5">
          <Shield size={14} className="text-[#00FF88]" />
        </div>
        <div className="leading-tight">
          <div className="text-[11px] font-black tracking-tight text-white font-['Chivo']">ARX MENTIS</div>
          <div className="text-[8px] uppercase tracking-[0.15em] text-[#00FF88] font-bold">Income Engine</div>
        </div>
      </div>
    </div>
  );
}

// ==================== SSE LIVE HOOK ====================
// Iter121.12 — Accepts an optional `watchlistId`. When provided, the SSE
// URL includes `?watchlist_id=<id>` so the backend scans that specific
// list; when null/undefined the backend follows the caller's active
// watchlist. The hook re-establishes the EventSource whenever the id
// changes so the operator's grid updates match immediately.
// Iter121.14 — Also exposes `scanProgress = { total, done, symbol,
// complete }` derived from the SSE `scan_start` / `result` /
// `scan_complete` events so the Dashboard can render a live
// "Scanning N/M · <ticker>" progress line.
export function useLiveStream(watchlistId = null) {
  const [results, setResults] = useState([]);
  const [status, setStatus] = useState("connecting");
  const [lastUpdate, setLastUpdate] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState({ total: 0, done: 0, symbol: null, complete: false });
  const esRef = useRef(null);

  useEffect(() => {
    // Reset results on watchlist switch so the grid doesn't briefly
    // paint stale cards from the previous list while the new SSE
    // cycle spins up.
    setResults([]);
    setStatus("connecting");
    setScanning(false);
    setScanProgress({ total: 0, done: 0, symbol: null, complete: false });
    const connect = () => {
      if (esRef.current) esRef.current.close();
      const url = watchlistId
        ? `${API}/scan/stream?watchlist_id=${encodeURIComponent(watchlistId)}`
        : `${API}/scan/stream`;
      const es = new EventSource(url, { withCredentials: true });
      esRef.current = es;

      es.onopen = () => setStatus("live");

      es.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === "scan_start") {
            setScanning(true);
            setScanProgress({ total: msg.count || (msg.symbols || []).length, done: 0, symbol: null, complete: false });
          } else if (msg.type === "result") {
            setResults(prev => {
              const filtered = prev.filter(r => r.symbol !== msg.data.symbol);
              const updated = [...filtered, msg.data];
              updated.sort((a, b) => (b.ivr || 0) - (a.ivr || 0));
              return updated;
            });
            setLastUpdate(new Date());
            setScanProgress(prev => ({
              total: prev.total,
              done: Math.min(prev.total || (prev.done + 1), prev.done + 1),
              symbol: msg.data?.symbol || null,
              complete: false,
            }));
          } else if (msg.type === "scan_complete") {
            setResults(msg.results);
            setLastUpdate(new Date());
            setScanning(false);
            setScanProgress(prev => ({
              total: prev.total || (msg.results?.length || 0),
              done: prev.total || (msg.results?.length || 0),
              symbol: null,
              complete: true,
            }));
          } else if (msg.type === "empty") {
            setScanning(false);
            setScanProgress({ total: 0, done: 0, symbol: null, complete: true });
          }
        } catch (e) { console.error("SSE parse error:", e); }
      };

      es.onerror = () => {
        setStatus("reconnecting");
        es.close();
        setTimeout(connect, 3000);
      };
    };

    connect();
    return () => { if (esRef.current) esRef.current.close(); };
  }, [watchlistId]);

  return { results, status, lastUpdate, scanning, scanProgress };
}

// ==================== Iter121.20 — LIVESTREAM CONTEXT ====================
// Lift the SSE up to the App level so the scan keeps running when the
// operator navigates away from Dashboard (to Market Forge, Settings,
// etc.) and comes back. The context owns ONE SSE connection per
// authenticated session — no more restart-on-remount cycles.
//
// How watchlist switches propagate:
//   - Any page that calls `switchActive` on its own `useActiveWatchlist`
//     instance dispatches a `arxmentis:active-watchlist-changed` window
//     event (see WatchlistPicker.jsx). This provider listens for that
//     event and updates its own `activeId`, which triggers
//     `useLiveStream(activeId)` to reconnect the SSE.
const LiveStreamCtx = React.createContext(null);

export function LiveStreamProvider({ children }) {
  // Bootstrap the active watchlist id once so the initial SSE URL is
  // already scoped correctly. Subsequent switches are picked up via
  // the window event bus (avoids duplicating the full watchlist
  // fetch — pages that already need the list keep their own hook).
  const [activeId, setActiveId] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/user/active-watchlist`);
        if (!cancelled) setActiveId(r.data?.id || null);
      } catch { /* silent — backend will fall through to caller's active pointer */ }
    })();
    const onSwitch = (e) => {
      const nextId = e?.detail?.activeId || null;
      if (nextId) setActiveId(nextId);
    };
    window.addEventListener("arxmentis:active-watchlist-changed", onSwitch);
    return () => {
      cancelled = true;
      window.removeEventListener("arxmentis:active-watchlist-changed", onSwitch);
    };
  }, []);

  const live = useLiveStream(activeId);
  const value = React.useMemo(() => ({ ...live, activeWatchlistId: activeId }), [live, activeId]);
  return <LiveStreamCtx.Provider value={value}>{children}</LiveStreamCtx.Provider>;
}

// Consumers (Dashboard) use this instead of calling `useLiveStream`
// directly. Falls back gracefully outside the provider so unit tests
// / storybook can still render pages standalone.
export function useSharedLiveStream() {
  const ctx = React.useContext(LiveStreamCtx);
  if (!ctx) {
    // Provider not mounted — return a safe empty envelope so callers
    // don't crash. In production the provider is always mounted at
    // AuthedRoutes level.
    return {
      results: [],
      status: "connecting",
      lastUpdate: null,
      scanning: false,
      scanProgress: { total: 0, done: 0, symbol: null, complete: false },
      activeWatchlistId: null,
    };
  }
  return ctx;
}

// ==================== TICKER CARD ====================
export function TickerLogo({ symbol }) {
  const [src, setSrc] = useState(`https://assets.parqet.com/logos/symbol/${symbol}`);
  const [fallback, setFallback] = useState(0);

  const handleError = () => {
    if (fallback === 0) {
      setSrc(`https://static2.finnhub.io/file/publicdatany/finnhubimage/stock_logo/${symbol}.png`);
      setFallback(1);
    } else {
      setFallback(2);
    }
  };

  if (fallback >= 2) return <div className="w-9 h-9 rounded-full bg-[#1A1A20] border border-[#26262B] flex items-center justify-center text-xs font-bold text-[#8A8A93] flex-shrink-0">{symbol[0]}</div>;
  return <img src={src} alt="" className="w-9 h-9 rounded-full bg-[#1A1A20] border border-[#26262B] object-contain p-0.5 flex-shrink-0" onError={handleError} />;
}

export function CCChainDiagModal({ symbol, onClose, cycleData, autoFetch = false, currentWoundPerShare = null, contracts = 1 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("table");
  const [kp, setKp] = useState(cycleData?.kp ?? "");
  const [pPrev, setPPrev] = useState(cycleData?.p_prev ?? "");
  const [dteP, setDteP] = useState(cycleData?.dte_p ?? "");
  const [submitted, setSubmitted] = useState(false);
  // Iter114.6 — "Tradeable opportunities" filter for the What-If
  // launcher.  When ON, narrows the chain to candidates that pass
  // ALL THREE hard gates:
  //   1. Strike inside the ±2σ tradeable range (`spot ± 2 ·
  //      sigma_1`) — filters out far-OTM lottery tickets.
  //   2. Wheel ITM doctrine (`passes_all && liq_pass`) — S1+S2+
  //      S3+S4 + Liquidity.
  //   3. Beats the operator's current Wound (only enforced when
  //      `currentWoundPerShare` is wired in — i.e. CC_OPEN state).
  // Pure client-side; visible only after the chain is loaded.
  const [onlyTradeable, setOnlyTradeable] = useState(false);
  const hasWoundContext = currentWoundPerShare != null;

  const fetchCC = useCallback(async (kpV = kp, ppV = pPrev, dpV = dteP) => {
    // Iter114.4 — accept 0-valued inputs (e.g. `dte_p = 0` when the
    // operator wants a pure call-side scenario test from the Cycle
    // Tracker What-If launcher).  Only reject empty strings / nulls.
    if (kpV === "" || kpV == null || ppV === "" || ppV == null || dpV === "" || dpV == null) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/chain-diag/cc?symbol=${symbol}&kp=${kpV}&p_prev=${ppV}&dte_p=${dpV}`);
      setData(r.data); setSubmitted(true);
    } catch (e) { alert(e.response?.data?.detail || "Error"); }
    setLoading(false);
  }, [symbol, kp, pPrev, dteP]);

  // Iter114.4 — auto-fetch the chain when the modal is launched
  // pre-filled from the Cycle Tracker What-If launcher.  Runs once
  // on mount.
  useEffect(() => {
    if (autoFetch && cycleData && cycleData.kp != null && cycleData.p_prev != null && cycleData.dte_p != null) {
      fetchCC(cycleData.kp, cycleData.p_prev, cycleData.dte_p);
    }
  }, []);

  const maxOI = data ? Math.max(...data.calls.map(c => c.oi || 0), 1) : 1;
  const maxVol = data ? Math.max(...data.calls.map(c => c.volume || 0), 1) : 1;
  const maxRoi = data ? Math.max(...data.calls.map(c => Math.abs(c.roi_cycle) || 0), 1) : 1;

  const HeatCell = ({ val, max, invert }) => {
    const norm = max > 0 ? Math.min(Math.abs(val) / max, 1) : 0;
    const intensity = invert ? (1 - norm) : norm;
    const r = invert ? Math.round(255 * norm) : Math.round(50 * (1 - norm));
    const g = invert ? Math.round(50 * (1 - norm)) : Math.round(255 * norm);
    return <td className="px-2 py-1.5 text-[10px] font-mono text-center" style={{ backgroundColor: `rgba(${r},${g},50,${0.1 + intensity * 0.35})`, color: intensity > 0.5 ? 'white' : '#8A8A93' }}>{typeof val === "number" ? (Math.abs(val) < 10 ? val.toFixed(1) : Math.round(val)) : val ?? "—"}</td>;
  };

  // Iter114.6 — Tradeable-opportunity helpers.  Computed AFTER the
  // chain is loaded so the ±2σ band is anchored to the actual
  // returned spot + atm_iv.
  const sigma2Lo = data?.spot != null && data?.sigma_1 != null
    ? Math.round((data.spot - 2 * data.sigma_1) * 100) / 100
    : null;
  const sigma2Hi = data?.spot != null && data?.sigma_1 != null
    ? Math.round((data.spot + 2 * data.sigma_1) * 100) / 100
    : null;
  const isInSigma2 = (strike) => sigma2Lo == null || sigma2Hi == null
    || (strike >= sigma2Lo && strike <= sigma2Hi);
  const isTradeable = (c) =>
    isInSigma2(c.strike)
    && c.passes_all
    && c.liq_pass
    && (!hasWoundContext || c.pl_cycle > currentWoundPerShare);
  const tradeableCount = data ? data.calls.filter(isTradeable).length : 0;

  // Iter114.7 — Order ticket preview helpers.  Pure read-only
  // mid-price arithmetic on the chain payload we already loaded.
  //   - `midOf(c)`        : (bid + ask) / 2 of any candidate row.
  //   - `currentOpenMid`  : mid of the operator's open Kc leg
  //     (looked up by `cycleData.kc_open` against the chain).
  //   - `netRollCredit(c)`: candidate mid minus current-leg mid
  //     (CC_OPEN) or just the candidate mid (AWAITING_CC).
  // Surfacing in the Verdict cell so the operator instantly sees
  // the realistic per-share fill if they sell-to-open at mid AND
  // (for rolls) buy-to-close the current leg at mid.
  const midOf = (c) => c != null && c.bid != null && c.ask != null
    ? Math.round(((c.bid + c.ask) / 2) * 100) / 100
    : null;
  const kcOpen = cycleData?.kc_open;
  const currentOpenRow = (data && kcOpen != null)
    ? data.calls.find(c => Math.abs(c.strike - kcOpen) < 0.01)
    : null;
  const currentOpenMid = midOf(currentOpenRow);
  const hasRollContext = currentOpenMid != null;
  const netRollCredit = (c) => {
    const newMid = midOf(c);
    if (newMid == null) return null;
    if (!hasRollContext) return newMid; // AWAITING_CC: just the open credit
    return Math.round((newMid - currentOpenMid) * 100) / 100;
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0A0A0D] border border-[#26262B] rounded-sm w-full max-w-[1600px] max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#26262B]">
          <div className="flex items-center gap-3">
            <TickerLogo symbol={symbol} />
            <div>
              <h3 className="text-lg font-mono font-bold text-white">{symbol} CC Chain Diagnostics</h3>
              {data && <p className="text-[10px] font-mono text-[#8A8A93]">EXP {data.expiration} ({data.dte_c}d) · DTE_total {data.dte_total}d · C_eff ${data.c_effective} · 1σ ${data.sigma_lo}—${data.sigma_hi}{sigma2Lo != null && sigma2Hi != null && <> · <span data-testid="cc-chain-sigma2-window" className="text-[#A78BFA]">2σ ${sigma2Lo}—${sigma2Hi}</span></>}</p>}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {data?.recommended_strike && <span className="text-[10px] px-2 py-1 rounded-sm font-mono font-bold border border-[#00FF88] text-[#00FF88] bg-[#00FF88]/10">REC K=${data.recommended_strike}</span>}
            {/* Iter114.6 — Tradeable opportunities filter chip.
                Visible whenever the chain is loaded.  Combines
                ±2σ band + Wheel ITM doctrine + (optionally) the
                "beats current Wound" gate when context is wired. */}
            {submitted && data && (
              <button
                type="button"
                data-testid="cc-chain-tradeable-opportunities-toggle"
                onClick={() => setOnlyTradeable(v => !v)}
                className={`text-[10px] px-2.5 py-1 rounded-sm font-mono font-bold border uppercase tracking-[0.12em] inline-flex items-center gap-1.5 transition-colors ${
                  onlyTradeable
                    ? "border-[#00FF88] text-[#00FF88] bg-[#00FF88]/10"
                    : "border-[#26262B] text-[#8A8A93] hover:border-[#00FF88]/40 hover:text-[#00FF88]"
                }`}
                title={`Tradeable filter: ±2σ band + Wheel ITM doctrine${hasWoundContext ? " + beats current Wound" : ""}`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${onlyTradeable ? "bg-[#00FF88]" : "bg-[#26262B]"}`} />
                Tradeable only ({tradeableCount})
              </button>
            )}
            {hasWoundContext && submitted && (
              <span
                data-testid="cc-chain-current-wound-pill"
                className="text-[10px] px-2 py-1 rounded-sm font-mono font-bold border border-[#A78BFA]/40 text-[#A78BFA] bg-[#A78BFA]/5"
              >
                Current Wound ${currentWoundPerShare.toFixed(2)}/sh
              </span>
            )}
            {/* Iter114.7 — Current-leg buy-back mid pill.  Shows the
                mid-price of the operator's open Kc so the order-ticket
                "Net roll" math is visible at a glance. */}
            {hasRollContext && submitted && (
              <span
                data-testid="cc-chain-current-cc-mid-pill"
                className="text-[10px] px-2 py-1 rounded-sm font-mono font-bold border border-[#FFB800]/40 text-[#FFB800] bg-[#FFB800]/5"
                title={`Mid-price to BUY back your current Kc=$${kcOpen} CC (used as the close-cost estimate in Net Roll)`}
              >
                Kc=${kcOpen} buy-back ${currentOpenMid.toFixed(2)}/sh
              </span>
            )}
            {/* Iter114.8 — contract count pill so the operator
                sees the position scale (and $-totals on every
                ticket) at a glance. */}
            {contracts > 1 && submitted && (
              <span
                data-testid="cc-chain-contracts-pill"
                className="text-[10px] px-2 py-1 rounded-sm font-mono font-bold border border-[#00FF88]/40 text-[#00FF88] bg-[#00FF88]/5"
                title="Contract count drives the $-total on every Net Roll / Open ticket"
              >
                {contracts} contracts
              </span>
            )}
            {submitted && <div className="flex border border-[#26262B] rounded-sm overflow-hidden">
              <button onClick={() => setView("table")} className={`px-3 py-1 text-[10px] font-bold ${view === "table" ? "bg-[#00FF88]/10 text-[#00FF88]" : "text-[#8A8A93]"}`}>Table</button>
              <button onClick={() => setView("heatmap")} className={`px-3 py-1 text-[10px] font-bold ${view === "heatmap" ? "bg-[#00FF88]/10 text-[#00FF88]" : "text-[#8A8A93]"}`}>Heatmap</button>
            </div>}
            <button onClick={onClose} className="text-[#8A8A93] hover:text-white"><X size={18} /></button>
          </div>
        </div>

        {/* Cycle context inputs */}
        {!submitted && (
          <div className="px-5 py-4 border-b border-[#26262B] bg-[#121216]">
            <p className="text-xs text-[#8A8A93] mb-3">Enter your cycle context from the CSP assignment:</p>
            <div className="grid grid-cols-4 gap-3">
              <div><label className="text-[10px] font-bold uppercase text-[#8A8A93] mb-1 block">Kp (CSP Strike)</label><input type="number" step="0.5" value={kp} onChange={e => setKp(e.target.value)} className="w-full bg-[#08080A] border border-[#26262B] text-white rounded-sm px-3 py-2 text-sm font-mono" placeholder="68.00" /></div>
              <div><label className="text-[10px] font-bold uppercase text-[#8A8A93] mb-1 block">P_prev (Premiums)</label><input type="number" step="0.01" value={pPrev} onChange={e => setPPrev(e.target.value)} className="w-full bg-[#08080A] border border-[#26262B] text-white rounded-sm px-3 py-2 text-sm font-mono" placeholder="3.52" /></div>
              <div><label className="text-[10px] font-bold uppercase text-[#8A8A93] mb-1 block">DTEp (Put Days)</label><input type="number" step="1" value={dteP} onChange={e => setDteP(e.target.value)} className="w-full bg-[#08080A] border border-[#26262B] text-white rounded-sm px-3 py-2 text-sm font-mono" placeholder="5" /></div>
              <div className="flex items-end"><button onClick={() => fetchCC()} disabled={loading || kp === "" || pPrev === "" || dteP === ""} className="w-full bg-[#00FF88] text-[#08080A] font-bold px-4 py-2 rounded-sm text-sm disabled:opacity-40">{loading ? "Analyzing..." : "Analyze CC"}</button></div>
            </div>
          </div>
        )}

        {/* Results */}
        {submitted && data && (
          <div className="flex-1 overflow-auto">
            {view === "table" ? (
              <table className="w-full text-[10px] font-mono">
                <thead className="sticky top-0 bg-[#0A0A0D] z-10">
                  <tr className="border-b border-[#26262B]">
                    <th className="px-2 py-2 text-left text-[#8A8A93] font-bold uppercase">Strike</th>
                    <th className="px-2 py-2 text-right text-[#8A8A93]">Delta</th>
                    <th className="px-2 py-2 text-right text-[#8A8A93]">Bid</th>
                    <th className="px-2 py-2 text-right text-[#8A8A93]">Ask</th>
                    <th className="px-2 py-2 text-right text-[#8A8A93]">Credit</th>
                    <th className="px-2 py-2 text-right text-[#8A8A93]">PL Cycle</th>
                    <th className="px-2 py-2 text-right text-[#8A8A93]">ROI%</th>
                    <th className="px-2 py-2 text-right text-[#8A8A93]">OI</th>
                    <th className="px-2 py-2 text-center text-[#8A8A93]">S1</th>
                    <th className="px-2 py-2 text-center text-[#8A8A93]">S2</th>
                    <th className="px-2 py-2 text-center text-[#8A8A93]">S3</th>
                    <th className="px-2 py-2 text-center text-[#8A8A93]">S4</th>
                    <th className="px-2 py-2 text-center text-[#8A8A93]">Liq</th>
                    <th className="px-2 py-2 text-center text-[#8A8A93]">Verdict</th>
                    {/* Iter114.9 — Order ticket moved out of the
                        Verdict cell into a dedicated column so the
                        per-share fill + dollar total are easier to
                        scan and the table layout stays clean. */}
                    <th data-testid="cc-chain-ticket-col-header" className="px-3 py-2 text-right text-[#A78BFA] uppercase tracking-[0.12em]">Ticket</th>
                  </tr>
                </thead>
                <tbody>
                  {data.calls
                    .filter(c => !onlyTradeable || isTradeable(c))
                    .map((c, i) => {
                    const isRec = c.is_recommended;
                    const isSpot = Math.abs(c.strike - data.spot) < 0.5;
                    const isSigma = c.in_sigma;
                    // Iter114.5 — Beats-current marker: row improves
                    // when its PL Cycle (= hypothetical Wound) is
                    // strictly greater than the operator's current
                    // Wound.  Independent of the Wheel ITM verdict
                    // — purely a "this trade beats your current
                    // position" signal.
                    const woundDelta = hasWoundContext ? c.pl_cycle - currentWoundPerShare : null;
                    const beatsCurrent = hasWoundContext && woundDelta > 0;
                    const rowBg = isRec ? "bg-[#00FF88]/10" : beatsCurrent ? "bg-[#A78BFA]/[0.05]" : isSpot ? "bg-[#007AFF]/10" : isSigma ? "bg-[#00FF88]/[0.02]" : "";
                    const borderL = isRec ? "border-l-2 border-l-[#00FF88]" : beatsCurrent ? "border-l-2 border-l-[#A78BFA]" : isSigma ? "border-l-2 border-l-[#00FF88]/20" : "border-l-2 border-l-transparent";
                    const StepIcon = ({pass}) => pass ? <CheckCircle2 size={10} className="text-[#00FF88] inline" /> : <XCircle size={10} className="text-[#FF3366] inline" />;
                    return (
                      <tr key={i} data-testid={`cc-chain-row-strike-${c.strike}`} className={`${rowBg} ${borderL} border-b border-[#1A1A20] hover:bg-[#1A1A20]/50`}>
                        <td className="px-2 py-1.5">
                          <span className={`font-bold ${isRec ? "text-[#00FF88]" : isSpot ? "text-[#007AFF]" : isSigma ? "text-white" : "text-[#8A8A93]"}`}>${c.strike}</span>
                          {isRec && <span className="ml-1 text-[8px] text-[#00FF88]">REC</span>}
                          {isSpot && <span className="ml-1 text-[8px] text-[#007AFF]">ATM</span>}
                          {beatsCurrent && !isRec && <span data-testid={`cc-chain-beats-marker-${c.strike}`} className="ml-1 text-[8px] text-[#A78BFA] font-bold">BEAT</span>}
                        </td>
                        <td className="px-2 py-1.5 text-right text-white">{c.delta != null ? c.delta.toFixed(4) : "—"}</td>
                        <td className="px-2 py-1.5 text-right text-white">${c.bid}</td>
                        <td className="px-2 py-1.5 text-right text-white">${c.ask}</td>
                        <td className="px-2 py-1.5 text-right text-white font-bold">${c.credit}</td>
                        <td className={`px-2 py-1.5 text-right font-bold ${c.pl_cycle > 0.5 ? "text-[#00FF88]" : c.pl_cycle > 0 ? "text-[#FFB800]" : "text-[#FF3366]"}`}>
                          <div className="inline-flex items-baseline gap-1.5 justify-end">
                            <span>${c.pl_cycle}</span>
                            {hasWoundContext && woundDelta != null && (
                              <span
                                data-testid={`cc-chain-wound-delta-${c.strike}`}
                                className={`text-[8px] font-mono px-1 py-0.5 rounded-sm ${
                                  woundDelta > 0
                                    ? "text-[#A78BFA] bg-[#A78BFA]/10 border border-[#A78BFA]/30"
                                    : woundDelta < 0
                                      ? "text-[#FF5577]/70 bg-[#FF5577]/5 border border-[#FF5577]/20"
                                      : "text-[#8A8A93] bg-[#1A1A20] border border-[#26262B]"
                                }`}
                              >
                                {woundDelta > 0 ? "↑" : woundDelta < 0 ? "↓" : "→"}{woundDelta >= 0 ? "+" : ""}${woundDelta.toFixed(2)}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className={`px-2 py-1.5 text-right font-bold ${c.roi_cycle >= 3 ? "text-[#00FF88]" : c.roi_cycle > 0 ? "text-[#FFB800]" : "text-[#FF3366]"}`}>{c.roi_cycle}%</td>
                        <td className={`px-2 py-1.5 text-right ${c.oi >= 500 ? "text-[#00FF88]" : c.oi >= 100 ? "text-[#FFB800]" : "text-[#FF3366]"}`}>{c.oi}</td>
                        <td className="px-2 py-1.5 text-center"><StepIcon pass={c.step1} /></td>
                        <td className="px-2 py-1.5 text-center"><StepIcon pass={c.step2} /></td>
                        <td className="px-2 py-1.5 text-center"><StepIcon pass={c.step3} /></td>
                        <td className="px-2 py-1.5 text-center"><StepIcon pass={c.step4} /></td>
                        <td className="px-2 py-1.5 text-center">{c.liq_pass ? <CheckCircle2 size={10} className="text-[#00FF88] inline" /> : <span className="text-[#FF3366] text-[8px]">{c.liq_reason}</span>}</td>
                        <td className="px-2 py-1.5 text-center" data-testid={`cc-chain-verdict-${c.strike}`}>
                          {isRec ? <span className="text-[10px] font-bold text-[#00FF88]">SELECT</span> :
                           c.passes_all && c.liq_pass ? <span className="text-[10px] text-[#00FF88]">VALID</span> :
                           c.passes_mandatory && c.liq_pass ? <span className="text-[10px] text-[#FFB800]">OK*</span> :
                           <span className="text-[10px] text-[#FF3366]">REJECT</span>}
                        </td>
                        {/* Iter114.9 — Dedicated TICKET column.  Renders
                            only on SELECT / VALID rows; REJECT rows
                            show a muted "—" so the column reads cleanly. */}
                        <td className="px-3 py-1.5 text-right whitespace-nowrap border-l border-[#1A1A20]">
                          {(isRec || (c.passes_all && c.liq_pass)) && netRollCredit(c) != null ? (
                            <div
                              data-testid={`cc-chain-ticket-${c.strike}`}
                              className="text-[10px] font-mono leading-tight"
                              title={hasRollContext
                                ? `Net roll = candidate mid $${midOf(c).toFixed(2)} − current Kc=$${kcOpen} mid $${currentOpenMid.toFixed(2)} (× 100 × ${contracts} contracts)`
                                : `Open-to-sell credit at mid $${midOf(c).toFixed(2)} (× 100 × ${contracts} contracts)`}
                            >
                              <div className={`font-bold ${netRollCredit(c) >= 0 ? "text-[#A78BFA]" : "text-[#FF5577]"}`}>
                                {hasRollContext ? "Net roll " : "Open "}
                                {netRollCredit(c) >= 0 ? "+" : ""}${netRollCredit(c).toFixed(2)}/sh
                              </div>
                              <div
                                data-testid={`cc-chain-ticket-total-${c.strike}`}
                                className={`text-[9px] mt-0.5 ${netRollCredit(c) >= 0 ? "text-[#A78BFA]/70" : "text-[#FF5577]/70"}`}
                              >
                                {netRollCredit(c) >= 0 ? "+" : ""}${(netRollCredit(c) * 100 * contracts).toFixed(0)}
                                <span className="text-[#5F5F66]"> · {contracts} ct</span>
                              </div>
                            </div>
                          ) : (
                            <span className="text-[9px] text-[#3A3A40]">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <table className="w-full text-[10px] font-mono">
                <thead className="sticky top-0 bg-[#0A0A0D] z-10">
                  <tr className="border-b border-[#26262B]">
                    <th className="px-2 py-2 text-left text-[#8A8A93]">Strike</th>
                    <th className="px-2 py-2 text-center text-[#8A8A93]">Delta</th>
                    <th className="px-2 py-2 text-center text-[#8A8A93]">Spread%</th>
                    <th className="px-2 py-2 text-center text-[#8A8A93]">OI</th>
                    <th className="px-2 py-2 text-center text-[#8A8A93]">PL Cycle</th>
                    <th className="px-2 py-2 text-center text-[#8A8A93]">ROI%</th>
                    <th className="px-2 py-2 text-center text-[#8A8A93]">Verdict</th>
                  </tr>
                </thead>
                <tbody>
                  {data.calls
                    .filter(c => !onlyTradeable || isTradeable(c))
                    .map((c, i) => {
                    const isRec = c.is_recommended;
                    const isSigma = c.in_sigma;
                    const isSpot = Math.abs(c.strike - data.spot) < 0.5;
                    const borderL = isRec ? "border-l-2 border-l-[#00FF88]" : isSigma ? "border-l-2 border-l-[#00FF88]/20" : "border-l-2 border-l-transparent";
                    return (
                      <tr key={i} className={`${borderL} border-b border-[#1A1A20]/50`}>
                        <td className="px-2 py-1.5">
                          <span className={`font-bold ${isRec ? "text-[#00FF88]" : isSpot ? "text-[#007AFF]" : isSigma ? "text-white" : "text-[#8A8A93]"}`}>${c.strike}</span>
                          {isRec && <span className="ml-1 text-[8px] text-[#00FF88]">REC</span>}
                        </td>
                        <HeatCell val={c.delta || 0} max={1} invert={false} />
                        <HeatCell val={c.spread_pct || 0} max={20} invert={true} />
                        <HeatCell val={c.oi || 0} max={maxOI} invert={false} />
                        <HeatCell val={c.pl_cycle} max={Math.max(...data.calls.map(x => Math.abs(x.pl_cycle)))} invert={false} />
                        <HeatCell val={c.roi_cycle} max={maxRoi} invert={false} />
                        <td className="px-2 py-1.5 text-center text-[10px] font-bold">
                          {isRec ? <span className="text-[#00FF88]">SELECT</span> :
                           c.passes_all && c.liq_pass ? <span className="text-[#00FF88]">VALID</span> :
                           c.passes_mandatory ? <span className="text-[#FFB800]">OK*</span> :
                           <span className="text-[#FF3366]">REJ</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Footer */}
        {submitted && data && (
          <div className="flex items-center gap-4 px-5 py-2.5 border-t border-[#26262B] text-[9px] font-mono text-[#8A8A93]">
            <span>S1: DTE{"\u2264"}20 · S2: PL{">"}0 · S3: ROI{"\u2265"}3% · S4: PL{"\u2265"}$0.50</span>
            <span className="ml-auto">
              {data.calls.filter(c => c.passes_all && c.liq_pass).length} pass all ·
              {data.calls.filter(c => c.passes_mandatory && c.liq_pass).length} pass S1-3 ·
              {data.calls.filter(c => c.liq_pass).length} pass liquidity
              <span data-testid="cc-chain-tradeable-count" className="ml-2 text-[#00FF88] font-bold">
                · {tradeableCount} tradeable opportunit{tradeableCount === 1 ? "y" : "ies"}
                {hasWoundContext && " (beats Wound)"}
              </span>
            </span>
            <button onClick={() => setSubmitted(false)} className="text-[#007AFF] hover:text-white">Edit Inputs</button>
          </div>
        )}
      </div>
    </div>
  );
}


export function ChainDiagModal({ symbol, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("table"); // "table" | "heatmap"

  useEffect(() => {
    axios.get(`${API}/chain-diag?symbol=${symbol}`).then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [symbol]);

  if (loading) return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center" onClick={onClose}>
      <div className="bg-[#121216] border border-[#26262B] rounded-sm p-8"><Loader2 className="animate-spin text-[#00FF88]" size={28} /></div>
    </div>
  );
  if (!data || data.error) return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center" onClick={onClose}>
      <div className="bg-[#121216] border border-[#26262B] rounded-sm p-8 text-[#FF3366] text-sm">{data?.error || "No data"}<button onClick={onClose} className="ml-4 text-[#8A8A93]"><X size={16} /></button></div>
    </div>
  );

  const sigmaPuts = data.puts.filter(p => p.in_sigma);
  const allPuts = data.puts;
  const maxOI = Math.max(...allPuts.map(p => p.oi || 0), 1);
  const maxVol = Math.max(...allPuts.map(p => p.volume || 0), 1);
  const maxRoi = Math.max(...allPuts.map(p => p.roi || 0), 1);

  const HeatCell = ({ val, max, invert }) => {
    const norm = max > 0 ? Math.min(val / max, 1) : 0;
    const intensity = invert ? (1 - norm) : norm;
    const r = invert ? Math.round(255 * norm) : Math.round(50 * (1 - norm));
    const g = invert ? Math.round(50 * (1 - norm)) : Math.round(255 * norm);
    return <td className="px-2 py-1.5 text-[10px] font-mono text-center" style={{ backgroundColor: `rgba(${r},${g},50,${0.1 + intensity * 0.35})`, color: intensity > 0.5 ? 'white' : '#8A8A93' }}>{typeof val === "number" ? (val < 10 ? val.toFixed(1) : Math.round(val)) : val ?? "—"}</td>;
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0A0A0D] border border-[#26262B] rounded-sm w-full max-w-5xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#26262B]">
          <div className="flex items-center gap-3">
            <TickerLogo symbol={symbol} />
            <div>
              <h3 className="text-lg font-mono font-bold text-white">{data.symbol} Chain Diagnostics</h3>
              <p className="text-[10px] font-mono text-[#8A8A93]">EXP {data.expiration} ({data.dte}d) · ATM IV {data.atm_iv}% · 1σ ${data.sigma_lo}—${data.sigma_hi} (±${data.sigma_1})</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`text-[10px] px-2 py-1 rounded-sm font-mono font-bold border ${data.put_75d_inside_sigma ? "border-[#00FF88] text-[#00FF88] bg-[#00FF88]/10" : "border-[#FF3366] text-[#FF3366] bg-[#FF3366]/10"}`}>
              75{"\u0394"} K=${data.put_75d_strike} {data.put_75d_inside_sigma ? "IN 1σ" : "OUTSIDE 1σ"}
            </span>
            <div className="flex border border-[#26262B] rounded-sm overflow-hidden">
              <button onClick={() => setView("table")} className={`px-3 py-1 text-[10px] font-bold ${view === "table" ? "bg-[#00FF88]/10 text-[#00FF88]" : "text-[#8A8A93]"}`}>Table</button>
              <button onClick={() => setView("heatmap")} className={`px-3 py-1 text-[10px] font-bold ${view === "heatmap" ? "bg-[#00FF88]/10 text-[#00FF88]" : "text-[#8A8A93]"}`}>Heatmap</button>
            </div>
            <button onClick={onClose} className="text-[#8A8A93] hover:text-white"><X size={18} /></button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto">
          {view === "table" ? (
            <table className="w-full text-[10px] font-mono">
              <thead className="sticky top-0 bg-[#0A0A0D] z-10">
                <tr className="border-b border-[#26262B]">
                  <th className="px-2 py-2 text-left text-[#8A8A93] font-bold uppercase tracking-wider">Strike</th>
                  <th className="px-2 py-2 text-right text-[#8A8A93]">Delta</th>
                  <th className="px-2 py-2 text-right text-[#8A8A93]">Bid</th>
                  <th className="px-2 py-2 text-right text-[#8A8A93]">Ask</th>
                  <th className="px-2 py-2 text-right text-[#8A8A93]">Spread</th>
                  <th className="px-2 py-2 text-right text-[#8A8A93]">Spread%</th>
                  <th className="px-2 py-2 text-right text-[#8A8A93]">OI</th>
                  <th className="px-2 py-2 text-right text-[#8A8A93]">Vol</th>
                  <th className="px-2 py-2 text-right text-[#8A8A93]">IV</th>
                  <th className="px-2 py-2 text-right text-[#8A8A93]">Credit</th>
                  <th className="px-2 py-2 text-right text-[#8A8A93]">ROI%</th>
                  <th className="px-2 py-2 text-center text-[#8A8A93]">Liq</th>
                </tr>
              </thead>
              <tbody>
                {allPuts.map((p, i) => {
                  const isSigma = p.in_sigma;
                  const is75 = p.is_75d;
                  const isSpot = Math.abs(p.strike - data.spot) < 0.5;
                  const rowBg = is75 ? "bg-[#FFB800]/10" : isSpot ? "bg-[#007AFF]/10" : isSigma ? "bg-[#00FF88]/[0.03]" : "";
                  const borderL = is75 ? "border-l-2 border-l-[#FFB800]" : isSigma ? "border-l-2 border-l-[#00FF88]/30" : "border-l-2 border-l-transparent";
                  return (
                    <tr key={i} className={`${rowBg} ${borderL} border-b border-[#1A1A20] hover:bg-[#1A1A20]/50`}>
                      <td className="px-2 py-1.5">
                        <span className={`font-bold ${is75 ? "text-[#FFB800]" : isSpot ? "text-[#007AFF]" : isSigma ? "text-white" : "text-[#8A8A93]"}`}>${p.strike}</span>
                        {is75 && <span className="ml-1 text-[8px] text-[#FFB800]">75{"\u0394"}</span>}
                        {isSpot && <span className="ml-1 text-[8px] text-[#007AFF]">ATM</span>}
                      </td>
                      <td className="px-2 py-1.5 text-right text-white">{p.delta != null ? p.delta.toFixed(4) : "—"}</td>
                      <td className="px-2 py-1.5 text-right text-white">${p.bid}</td>
                      <td className="px-2 py-1.5 text-right text-white">${p.ask}</td>
                      <td className="px-2 py-1.5 text-right text-white">${p.spread}</td>
                      <td className={`px-2 py-1.5 text-right ${p.spread_pct != null && p.spread_pct > 15 ? "text-[#FF3366]" : p.spread_pct > 5 ? "text-[#FFB800]" : "text-[#00FF88]"}`}>{p.spread_pct != null ? p.spread_pct + "%" : "—"}</td>
                      <td className={`px-2 py-1.5 text-right ${p.oi >= 500 ? "text-[#00FF88]" : p.oi >= 100 ? "text-[#FFB800]" : "text-[#FF3366]"}`}>{p.oi}</td>
                      <td className="px-2 py-1.5 text-right text-[#8A8A93]">{p.volume || 0}</td>
                      <td className="px-2 py-1.5 text-right text-[#8A8A93]">{p.iv != null ? (p.iv * 100).toFixed(1) + "%" : "—"}</td>
                      <td className="px-2 py-1.5 text-right text-white font-bold">${p.credit}</td>
                      <td className={`px-2 py-1.5 text-right font-bold ${p.roi >= 3 ? "text-[#00FF88]" : p.roi >= 2 ? "text-[#FFB800]" : "text-[#8A8A93]"}`}>{p.roi}%</td>
                      <td className="px-2 py-1.5 text-center">{p.liq_pass ? <CheckCircle2 size={10} className="text-[#00FF88] inline" /> : <span className="text-[#FF3366] text-[8px]">{p.liq_reason}</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            /* Heatmap view */
            <table className="w-full text-[10px] font-mono">
              <thead className="sticky top-0 bg-[#0A0A0D] z-10">
                <tr className="border-b border-[#26262B]">
                  <th className="px-2 py-2 text-left text-[#8A8A93]">Strike</th>
                  <th className="px-2 py-2 text-center text-[#8A8A93]">|Delta|</th>
                  <th className="px-2 py-2 text-center text-[#8A8A93]">Spread%</th>
                  <th className="px-2 py-2 text-center text-[#8A8A93]">OI</th>
                  <th className="px-2 py-2 text-center text-[#8A8A93]">Volume</th>
                  <th className="px-2 py-2 text-center text-[#8A8A93]">ROI%</th>
                  <th className="px-2 py-2 text-center text-[#8A8A93]">IV%</th>
                </tr>
              </thead>
              <tbody>
                {allPuts.map((p, i) => {
                  const is75 = p.is_75d;
                  const isSigma = p.in_sigma;
                  const isSpot = Math.abs(p.strike - data.spot) < 0.5;
                  const borderL = is75 ? "border-l-2 border-l-[#FFB800]" : isSigma ? "border-l-2 border-l-[#00FF88]/30" : "border-l-2 border-l-transparent";
                  const maxSpread = 20; // 20% is very wide
                  return (
                    <tr key={i} className={`${borderL} border-b border-[#1A1A20]/50`}>
                      <td className="px-2 py-1.5">
                        <span className={`font-bold ${is75 ? "text-[#FFB800]" : isSpot ? "text-[#007AFF]" : isSigma ? "text-white" : "text-[#8A8A93]"}`}>${p.strike}</span>
                        {is75 && <span className="ml-1 text-[8px] text-[#FFB800]">75{"\u0394"}</span>}
                        {isSpot && <span className="ml-1 text-[8px] text-[#007AFF]">ATM</span>}
                      </td>
                      <HeatCell val={p.delta != null ? Math.abs(p.delta) : 0} max={1} invert={false} />
                      <HeatCell val={p.spread_pct || 0} max={maxSpread} invert={true} />
                      <HeatCell val={p.oi || 0} max={maxOI} invert={false} />
                      <HeatCell val={p.volume || 0} max={maxVol} invert={false} />
                      <HeatCell val={p.roi || 0} max={maxRoi} invert={false} />
                      <HeatCell val={p.iv ? p.iv * 100 : 0} max={200} invert={false} />
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer legend */}
        <div className="flex items-center gap-4 px-5 py-2.5 border-t border-[#26262B] text-[9px] font-mono text-[#8A8A93]">
          <span className="flex items-center gap-1"><div className="w-2 h-2 bg-[#FFB800]/40 border border-[#FFB800]" /> 75{"\u0394"} Strike</span>
          <span className="flex items-center gap-1"><div className="w-2 h-2 bg-[#007AFF]/40 border border-[#007AFF]" /> ATM</span>
          <span className="flex items-center gap-1"><div className="w-2 h-2 border-l-2 border-l-[#00FF88]/50" /> 1σ Range</span>
          <span className="ml-auto">{sigmaPuts.length} puts in 1σ · {allPuts.filter(p => p.liq_pass).length} pass liquidity</span>
        </div>
      </div>
    </div>
  );
}

export function TickerCard({ r, showTradeBtn = true }) {
  const isOk = r.recommendation === "OK";
  const winnerScenario = r.winner === "ATM_CSP_ITM_CC" ? r.scenario_b : r.scenario_a;
  const winnerLabel = r.winner === "ATM_CSP_ITM_CC" ? "ATM CSP ITM CC" : "ATM CSP ATM CC";
  const wesH = r.wes_hourly || r.wes;
  const wesD = r.wes_daily;
  const hasAnyWes = wesH || wesD;
  const bestPct = winnerScenario?.income_pct || 0;
  const meetsThreshold = bestPct >= 3;
  const csp75 = r.csp_75d;
  const csp75Meets = csp75 && csp75.roi >= 3;
  const [showTrade, setShowTrade] = useState(false);
  const [showChain, setShowChain] = useState(false);
  const [showCCChain, setShowCCChain] = useState(false);

  const RegimeBadge = ({ w, label }) => {
    if (!w) return null;
    const cls = w.regime === "Bullish" ? "border-[#00FF88]/40 text-[#00FF88] bg-[#00FF88]/5" : w.regime === "Bearish" ? "border-[#FF3366]/40 text-[#FF3366] bg-[#FF3366]/5" : w.regime === "Mixed" ? "border-[#FFB800]/40 text-[#FFB800] bg-[#FFB800]/5" : "border-[#3F3F47] text-[#8A8A93]";
    return <span className={`text-[10px] px-2 py-0.5 rounded-sm font-mono font-bold border ${cls}`}>{label} {w.regime_signs} {w.regime}</span>;
  };

  return (
    <>
    <div id={`ticker-${r.symbol}`} data-testid={`ticker-card-${r.symbol}`} className={`bg-[#121216] border rounded-sm overflow-hidden animate-slide-in ${meetsThreshold && isOk ? "border-[#00FF88]/30" : "border-[#26262B]"}`}>
      <div className="flex items-center justify-between px-5 py-3 gap-4">
        {/* Left: Logo, ticker, spot, exp, IVR */}
        <div className="flex items-center gap-2 flex-wrap flex-shrink-0">
          <TickerLogo symbol={r.symbol} />
          <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${isOk ? "bg-[#00FF88]" : "bg-[#FF3366]"}`} />
          <span className="text-lg font-mono font-bold text-white">{r.symbol}</span>
          {/* Iter121.20 — Open the ticker in Market Forge in a NEW TAB
              so the Dashboard's live scan state stays intact. The
              default click on the card still navigates in-tab; this
              secondary affordance is for operators who want to keep
              their ranked grid visible while drilling in. */}
          <button
            type="button"
            data-testid={`ticker-open-forge-newtab-${r.symbol}`}
            onClick={(e) => {
              e.stopPropagation();
              window.open(`/forge?symbol=${encodeURIComponent(r.symbol)}`, "_blank", "noopener,noreferrer");
            }}
            title={`Open ${r.symbol} in Market Forge (new tab) — keeps this Dashboard scan alive`}
            className="p-1 rounded-sm border border-[#26262B] text-[#8A8A93] hover:text-[#00FF88] hover:border-[#00FF88]/40 hover:bg-[#00FF88]/8 transition-colors"
          >
            <ExternalLink size={11} />
          </button>
          {r.spot > 0 && <span className="text-sm font-mono text-[#8A8A93]">${r.spot?.toFixed(2)}</span>}
          {r.expiration && <span className="text-[10px] px-2 py-0.5 border border-[#3F3F47] text-[#8A8A93] rounded-sm font-mono">EXP {r.expiration} ({r.dte}d)</span>}
          {r.ivr != null && (
            <span className={`text-[10px] px-2 py-0.5 rounded-sm font-mono font-bold border ${r.ivr >= 50 ? "border-[#00FF88]/40 text-[#00FF88] bg-[#00FF88]/10" : r.ivr >= 30 ? "border-[#FFB800]/40 text-[#FFB800] bg-[#FFB800]/10" : "border-[#3F3F47] text-[#8A8A93]"}`}>
              IVR {r.ivr}{r.ivx ? ` | IVx ${r.ivx}%` : ""}
            </span>
          )}
          {meetsThreshold && isOk && <span className="text-[10px] px-2 py-0.5 rounded-sm font-mono font-bold border border-[#00FF88] text-[#00FF88] bg-[#00FF88]/15 tracking-wider animate-pulse-glow">CSP &gt;3%</span>}
        </div>
        {/* Center: WES badges */}
        {hasAnyWes && (
          <div className="flex items-center gap-1.5 flex-wrap justify-center flex-1 min-w-0">
            {/* Iter42 — Engine source badge. Surfaces PARITY (green) for
                normal production rows; LGY (amber) for legacy/diagnostic;
                ERR (red) when parity was unavailable. Mirrors the rule
                that no module may silently render legacy WES as official. */}
            <WesEngineBadge
              engine={wesH?.wes_engine || wesD?.wes_engine}
              parityValid={(wesH || wesD)?.wes_parity_valid}
              freshnessState={(wesH || wesD)?.freshness_state}
              scannedAt={(wesH || wesD)?.scanned_at}
              source={(wesH || wesD)?.source}
              testid={`ticker-engine-${r.symbol}`}
            />
            {wesD && <>
              <span className="text-[9px] font-bold uppercase tracking-[0.12em] text-[#8A8A93]">D</span>
              <WESBadge label="P" score={wesD.wes_p} advice={wesD.wes_p_advice} />
              <WESBadge label="C" score={wesD.wes_c} advice={wesD.wes_c_advice} />
              <RegimeBadge w={wesD} label="D" />
              {wesD.aggression && <AggressionBadge agg={wesD.aggression} tf="D" />}
            </>}
            {wesD && wesH && <div className="w-px h-4 bg-[#26262B] mx-0.5" />}
            {wesH && <>
              <span className="text-[9px] font-bold uppercase tracking-[0.12em] text-[#8A8A93]">H</span>
              <WESBadge label="P" score={wesH.wes_p} advice={wesH.wes_p_advice} />
              <WESBadge label="C" score={wesH.wes_c} advice={wesH.wes_c_advice} />
              <RegimeBadge w={wesH} label="H" />
              {wesH.aggression && <AggressionBadge agg={wesH.aggression} tf="H" />}
            </>}
          </div>
        )}
        {/* Right: CSP 75d ROI + Winner */}
        <div className="flex items-center gap-4 flex-shrink-0">
          {csp75 && (
            <div data-testid={`csp75d-${r.symbol}`} className="text-right mr-2">
              <span className={`text-xl font-mono font-bold ${csp75Meets ? "text-[#00FF88]" : csp75.roi >= 2 ? "text-[#FFB800]" : "text-[#8A8A93]"}`}>{csp75.roi}%</span>
              <p className="text-[9px] text-[#8A8A93] uppercase tracking-wider">CSP 75{"\u0394"} ROI</p>
            </div>
          )}
          {isOk && winnerScenario && (<div className="text-right"><span className={`text-2xl font-mono font-bold ${meetsThreshold ? "text-[#00FF88]" : "text-white"}`}>{winnerScenario.income_pct?.toFixed(2)}%</span><p className="text-[10px] text-[#8A8A93] uppercase tracking-wider">{winnerLabel}</p></div>)}
          {!isOk && <div className="flex items-center gap-2 text-[#FF3366]"><XCircle size={16} /><span className="text-xs font-mono">{r.why || "DATA_INCOMPLETE"}</span></div>}
        </div>
      </div>
      {/* Dual WES levels detail row */}
      {hasAnyWes && (
        <div className="px-5 py-2 border-t border-[#26262B] bg-[#0A0A0D] text-[10px] font-mono text-[#8A8A93] space-y-1">
          {wesD && (
            <div className="flex items-center gap-4">
              <span className="text-[9px] font-bold uppercase tracking-[0.1em] text-[#8A8A93] w-4">D</span>
              <span>VAL <span className="text-white">${Number(wesD.val).toFixed(2)}</span></span>
              <span>POC <span className="text-white">${Number(wesD.poc).toFixed(2)}</span></span>
              <span>VAH <span className="text-white">${Number(wesD.vah).toFixed(2)}</span></span>
              <span>{"\u0394"}VAL <span className={wesD.delta_val_pct >= 0 ? "text-[#00FF88]" : "text-[#FF3366]"}>{wesD.delta_val_pct > 0 ? "+" : ""}{Number(wesD.delta_val_pct).toFixed(2)}%</span></span>
              <span>{"\u0394"}VAH <span className={wesD.delta_vah_pct >= 0 ? "text-[#00FF88]" : "text-[#FF3366]"}>{wesD.delta_vah_pct > 0 ? "+" : ""}{Number(wesD.delta_vah_pct).toFixed(2)}%</span></span>
              <span>ATR <span className="text-white">${Number(wesD.atr).toFixed(2)}</span></span>
              <span className="text-[#3F3F47]">({wesD.bars_used} bars)</span>
            </div>
          )}
          {wesH && (
            <div className="flex items-center gap-4">
              <span className="text-[9px] font-bold uppercase tracking-[0.1em] text-[#8A8A93] w-4">H</span>
              <span>VAL <span className="text-white">${Number(wesH.val).toFixed(2)}</span></span>
              <span>POC <span className="text-white">${Number(wesH.poc).toFixed(2)}</span></span>
              <span>VAH <span className="text-white">${Number(wesH.vah).toFixed(2)}</span></span>
              <span>{"\u0394"}VAL <span className={wesH.delta_val_pct >= 0 ? "text-[#00FF88]" : "text-[#FF3366]"}>{wesH.delta_val_pct > 0 ? "+" : ""}{Number(wesH.delta_val_pct).toFixed(2)}%</span></span>
              <span>{"\u0394"}VAH <span className={wesH.delta_vah_pct >= 0 ? "text-[#00FF88]" : "text-[#FF3366]"}>{wesH.delta_vah_pct > 0 ? "+" : ""}{Number(wesH.delta_vah_pct).toFixed(2)}%</span></span>
              <span>ATR <span className="text-white">${Number(wesH.atr).toFixed(2)}</span></span>
              <span className="text-[#3F3F47]">({wesH.bars_used} bars)</span>
            </div>
          )}
        </div>
      )}
      {/* CSP 75 Delta detail row */}
      {csp75 && (
        <div className="flex items-center gap-4 px-5 py-2 border-t border-[#26262B] bg-[#0D0D10]">
          <span className={`text-[10px] px-2 py-0.5 rounded-sm font-mono font-bold border ${csp75Meets ? "border-[#00FF88]/50 text-[#00FF88] bg-[#00FF88]/10" : csp75.roi >= 2 ? "border-[#FFB800]/40 text-[#FFB800] bg-[#FFB800]/5" : "border-[#3F3F47] text-[#8A8A93]"}`}>CSP 75{"\u0394"}</span>
          <span className="text-[10px] font-mono text-[#8A8A93]">K <span className="text-white">${csp75.strike}</span></span>
          <span className="text-[10px] font-mono text-[#8A8A93]">Credit <span className="text-white">${csp75.credit}</span></span>
          <span className="text-[10px] font-mono text-[#8A8A93]">Bid/Ask <span className="text-white">${csp75.bid}/{csp75.ask}</span></span>
          {csp75.delta != null && <span className="text-[10px] font-mono text-[#8A8A93]">{"\u0394"} <span className="text-white">{csp75.delta}</span></span>}
          <span className="text-[10px] font-mono text-[#8A8A93]">OI <span className="text-white">{csp75.oi}</span></span>
          <span className={`text-[10px] font-mono font-bold ${csp75Meets ? "text-[#00FF88]" : csp75.roi >= 2 ? "text-[#FFB800]" : "text-[#8A8A93]"}`}>ROI {csp75.roi}%</span>
        </div>
      )}
      {isOk && (r.scenario_a || r.scenario_b) && (<div className="grid grid-cols-2 border-t border-[#26262B]">
        <ScenarioPanel scenario={r.scenario_a} isWinner={r.winner === "ATM_CSP_ATM_CC"} label="SCENARIO A: ATM + ATM" side="left" />
        <ScenarioPanel scenario={r.scenario_b} isWinner={r.winner === "ATM_CSP_ITM_CC"} label="SCENARIO B: ATM + ITM" side="right" />
      </div>)}
      {r.filtered_out?.length > 0 && (<div className="px-5 py-2 border-t border-[#26262B]">{r.filtered_out.map((f, i) => <span key={i} className="text-[10px] font-mono text-[#FF3366] mr-3">{f.leg} K={f.strike} ({f.reason})</span>)}</div>)}
      {wesH && r.spot > 0 && (<div className="px-5 py-3 border-t border-[#26262B] flex items-center gap-4">
        <div className="flex-1"><WESMiniChart wes={wesH} spot={r.spot} wesDaily={wesD} /></div>
        <button onClick={() => setShowChain(true)} data-testid={`chain-diag-btn-${r.symbol}`} className="text-[10px] px-3 py-1.5 bg-[#007AFF]/10 border border-[#007AFF]/30 text-[#007AFF] rounded-sm font-bold hover:bg-[#007AFF]/20 transition-colors whitespace-nowrap">CSP Diag</button>
        <button onClick={() => setShowCCChain(true)} data-testid={`cc-diag-btn-${r.symbol}`} className="text-[10px] px-3 py-1.5 bg-[#FFB800]/10 border border-[#FFB800]/30 text-[#FFB800] rounded-sm font-bold hover:bg-[#FFB800]/20 transition-colors whitespace-nowrap">CC Diag</button>
        {isOk && showTradeBtn && winnerScenario && (<button onClick={() => setShowTrade(true)} className="text-[10px] px-3 py-1.5 bg-[#00FF88]/10 border border-[#00FF88]/30 text-[#00FF88] rounded-sm font-bold hover:bg-[#00FF88]/20 transition-colors whitespace-nowrap">Enter Trade</button>)}
      </div>)}
    </div>
    {showTrade && <EnterTradeModal r={r} onClose={() => setShowTrade(false)} onSaved={() => {}} />}
    {showChain && <ChainDiagModal symbol={r.symbol} onClose={() => setShowChain(false)} />}
    {showCCChain && <CCChainDiagModal symbol={r.symbol} onClose={() => setShowCCChain(false)} />}
    </>
  );
}

export function WESBadge({ label, score, advice }) {
  const isGo = score >= 8; const isWait = score >= 5;
  return <span className={`text-[10px] px-2 py-0.5 rounded-sm font-mono font-bold border ${isGo ? "border-[#00FF88]/50 text-[#00FF88] bg-[#00FF88]/10" : isWait ? "border-[#FFB800]/40 text-[#FFB800] bg-[#FFB800]/5" : "border-[#FF3366]/30 text-[#FF3366] bg-[#FF3366]/5"}`} title={advice}>{label} {score}</span>;
}

export function AggressionBadge({ agg, tf }) {
  if (!agg) return null;
  const isBull = agg.type === "Bull";
  // Turning Point vs Continuation:
  // Bull @ VAL = continuation (buyers defend support), Bull @ VAH = turning point (breakout)
  // Bear @ VAH = continuation (sellers defend resistance), Bear @ VAL = turning point (breakdown)
  const isTurningPoint = (isBull && agg.edge === "VAH") || (!isBull && agg.edge === "VAL");
  const signal = isTurningPoint ? "Turning Point" : "Continuation";
  return (
    <span
      data-testid={`aggression-${tf}`}
      className={`text-[10px] px-2 py-0.5 rounded-sm font-mono font-bold border animate-pulse ${
        isBull
          ? "border-[#00FF88] text-[#00FF88] bg-[#00FF88]/15"
          : "border-[#FF3366] text-[#FF3366] bg-[#FF3366]/15"
      }`}
      title={`${agg.type} aggression at ${agg.edge} (${signal}) | ΔVAL ${agg.d_val}% | ΔVAH ${agg.d_vah}%`}
    >
      {isBull ? "\u25B2" : "\u25BC"} {agg.type} @ {agg.edge} · {signal}
    </span>
  );
}

function ScenarioPanel({ scenario, isWinner, label, side }) {
  if (!scenario) return <div className={`p-5 ${side === "left" ? "border-r border-[#26262B]" : ""} bg-[#08080A]`}><span className="text-xs font-bold uppercase tracking-[0.1em] text-[#8A8A93]">{label}</span><p className="text-sm text-[#8A8A93] mt-3">Filtered out</p></div>;
  return (
    <div className={`p-5 ${side === "left" ? "border-r border-[#26262B]" : ""} ${isWinner ? "bg-[#00FF88]/[0.04] border-l-2 border-l-[#00FF88]" : "bg-[#08080A]"}`}>
      <div className="flex items-center justify-between mb-4"><span className="text-xs font-bold uppercase tracking-[0.1em] text-[#8A8A93]">{label}</span>{isWinner && <span className="text-[10px] px-2.5 py-1 border border-[#00FF88] text-[#00FF88] rounded-sm font-bold bg-[#00FF88]/10">WINNER</span>}</div>
      <div className={`text-4xl font-mono font-bold mb-5 ${isWinner ? "text-[#00FF88]" : "text-white"}`}>{scenario.income_pct?.toFixed(2)}%</div>
      <div className="space-y-3"><LegDetail label="CSP" strike={scenario.csp_strike} credit={scenario.csp_credit} bid={scenario.csp_bid} ask={scenario.csp_ask} delta={scenario.csp_delta} oi={scenario.csp_oi} /><LegDetail label="CC" strike={scenario.cc_strike} credit={scenario.cc_credit} bid={scenario.cc_bid} ask={scenario.cc_ask} delta={scenario.cc_delta} oi={scenario.cc_oi} /></div>
      <div className="flex items-center justify-between mt-5 pt-3 border-t border-[#26262B]"><span className="text-xs text-[#8A8A93] uppercase font-bold">Total Credit</span><span className="text-lg font-mono font-bold text-white">${scenario.total_credit?.toFixed(2)}</span></div>
    </div>
  );
}

function LegDetail({ label, strike, credit, bid, ask, delta, oi }) {
  return (<div className="flex items-center justify-between text-sm font-mono"><div className="flex items-center gap-3"><span className="font-bold text-[#8A8A93] w-7">{label}</span><span className="text-white">K={strike}</span></div><div className="flex items-center gap-3 text-[#8A8A93]"><span className="text-white font-bold">${credit?.toFixed(2)}</span><span className="text-[11px]">{bid?.toFixed(2)}/{ask?.toFixed(2)}</span>{delta != null && <span className="text-[11px]">{"\u0394"}{typeof delta === "number" ? delta.toFixed(2) : delta}</span>}{oi != null && <span className="text-[11px]">OI:{oi}</span>}</div></div>);
}

function WESMiniChart({ wes, spot, wesDaily }) {
  if (!wes || !wes.val || !wes.vah) return null;
  const { val, vah, poc } = wes;
  const vp = wes.vp || [];
  const dVal = wesDaily?.val;
  const dVah = wesDaily?.vah;
  const dPoc = wesDaily?.poc;
  const allVals = [val, vah, spot];
  if (dVal) allVals.push(dVal);
  if (dVah) allVals.push(dVah);
  vp.forEach(b => { if (b.p) allVals.push(b.p); });
  const padding = (vah - val) * 0.15;
  const lo = Math.min(...allVals) - padding;
  const hi = Math.max(...allVals) + padding;
  const range = hi - lo || 1;
  const pct = (v) => ((v - lo) / range) * 100;
  const chartH = 72;
  const vpBarW = vp.length > 0 ? Math.max(2, 100 / vp.length) : 3;

  return (
    <div className="relative bg-[#08080A] rounded-sm overflow-hidden border border-[#1A1A20]" style={{ height: chartH }}>
      {/* Volume Profile — vertical bars rising from bottom */}
      {vp.length > 0 && vp.map((bar, i) => {
        if (bar.n <= 0.01) return null;
        const x = pct(bar.p);
        const maxH = chartH * 0.85;
        const barH = Math.max(2, bar.n * maxH);
        const isBuyDom = bar.bp >= 0.5;
        const barColor = bar.poc
          ? 'rgba(255,184,0,0.6)'
          : bar.va
            ? (isBuyDom ? 'rgba(0,255,136,0.25)' : 'rgba(255,51,102,0.2)')
            : (isBuyDom ? 'rgba(0,255,136,0.12)' : 'rgba(255,51,102,0.1)');
        return (
          <div key={i} className="absolute" style={{
            left: `${x}%`, bottom: 0, width: `${vpBarW}%`, height: barH,
            background: barColor, transform: 'translateX(-50%)',
          }} />
        );
      })}
      {/* Hourly VA zone border */}
      <div className="absolute top-0 bottom-0 border-l border-r border-[#00FF88]/25" style={{ left: `${pct(val)}%`, width: `${pct(vah) - pct(val)}%` }} />
      {/* Daily VA zone overlay */}
      {dVal != null && dVah != null && (
        <div className="absolute top-0 bottom-0 border-l border-r border-dashed border-[#007AFF]/30" style={{ left: `${pct(dVal)}%`, width: `${pct(dVah) - pct(dVal)}%` }} />
      )}
      {dPoc != null && <div className="absolute top-0 bottom-0 w-px border-l border-dotted border-[#007AFF]/40" style={{ left: `${pct(dPoc)}%` }} />}
      {/* Hourly level lines */}
      <div className="absolute top-0 bottom-0 w-px bg-[#00FF88]/60" style={{ left: `${pct(val)}%` }} />
      <div className="absolute bottom-0 text-[7px] font-mono text-[#00FF88] leading-none pb-px" style={{ left: `${pct(val)}%`, transform: 'translateX(-50%)' }}>VAL</div>
      <div className="absolute top-0 bottom-0 w-px bg-[#FFB800]/70" style={{ left: `${pct(poc)}%` }} />
      <div className="absolute top-0 text-[7px] font-mono text-[#FFB800] leading-none pt-px" style={{ left: `${pct(poc)}%`, transform: 'translateX(-50%)' }}>POC</div>
      <div className="absolute top-0 bottom-0 w-px bg-[#FF3366]/60" style={{ left: `${pct(vah)}%` }} />
      <div className="absolute bottom-0 text-[7px] font-mono text-[#FF3366] leading-none pb-px" style={{ left: `${pct(vah)}%`, transform: 'translateX(-50%)' }}>VAH</div>
      {/* Spot marker */}
      <div className="absolute w-[7px] h-[7px] rounded-full bg-white border border-[#00FF88] shadow-[0_0_6px_rgba(0,255,136,0.6)]" style={{ top: chartH / 2 - 3, left: `${pct(spot)}%`, transform: 'translateX(-50%)' }} />
      <div className="absolute text-[9px] font-mono font-bold text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]" style={{ top: 2, left: `${pct(spot)}%`, transform: 'translateX(-50%)' }}>${spot.toFixed(2)}</div>
      {/* Daily labels */}
      {dVal != null && <div className="absolute text-[6px] font-mono text-[#007AFF]/70 leading-none" style={{ bottom: 10, left: `${pct(dVal)}%`, transform: 'translateX(-50%)' }}>D</div>}
      {dVah != null && <div className="absolute text-[6px] font-mono text-[#007AFF]/70 leading-none" style={{ bottom: 10, left: `${pct(dVah)}%`, transform: 'translateX(-50%)' }}>D</div>}
    </div>
  );
}

export function LiveIndicator({ status, scanning, lastUpdate }) {
  return (<div className="flex items-center gap-3">
    {lastUpdate && <span className="text-[10px] text-[#8A8A93] font-mono"><Clock size={10} className="inline mr-1" />{lastUpdate.toLocaleTimeString()}</span>}
    <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-[10px] font-mono font-bold ${status === "live" ? "bg-[#00FF88]/10 text-[#00FF88] border border-[#00FF88]/30" : status === "reconnecting" ? "bg-[#FFB800]/10 text-[#FFB800] border border-[#FFB800]/30" : "bg-[#FF3366]/10 text-[#FF3366] border border-[#FF3366]/30"}`}>
      <div className={`w-1.5 h-1.5 rounded-full ${status === "live" ? "bg-[#00FF88] animate-pulse" : "bg-[#FFB800]"}`} />
      {scanning ? "SCANNING" : status === "live" ? "LIVE" : "CONNECTING"}
    </div>
  </div>);
}

export function EnterTradeModal({ r, onClose, onSaved }) {
  const wes = r.wes_hourly || r.wes || {};
  const sc = r.winner === "ATM_CSP_ITM_CC" ? (r.scenario_b || r.scenario_a) : (r.scenario_a || r.scenario_b);
  const [kp, setKp] = useState(sc?.csp_strike || "");
  const [pp, setPp] = useState(sc?.csp_credit || "");
  const [comment, setComment] = useState(""); const [saving, setSaving] = useState(false);
  const kpNum = parseFloat(kp) || 0;
  const ppNum = parseFloat(pp) || 0;
  const roiCsp = kpNum > 0 ? ((ppNum / kpNum) * 100).toFixed(2) : "0";
  const handleSave = async () => { if (!kpNum || !ppNum) return; setSaving(true); try { await axios.post(`${API}/cycles`, { symbol: r.symbol, kp: kpNum, pp: ppNum, dte_put: r.dte || 0, spot_entry: r.spot, wes_p: wes.wes_p, wes_c: wes.wes_c, ivr: r.ivr, regime: wes.regime, comment }); onSaved(); onClose(); } catch (e) { alert(e.response?.data?.detail || "Error"); } setSaving(false); };
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-[#121216] border border-[#26262B] rounded-sm w-[460px]" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#26262B]"><h3 className="text-lg font-bold text-white font-['Chivo']">Open ITM Cycle — {r.symbol}</h3><button onClick={onClose} className="text-[#8A8A93] hover:text-white"><X size={18} /></button></div>
        <div className="p-5 space-y-4">
          <p className="text-xs text-[#8A8A93]">Phase 1: Sell Cash-Secured Put (CSP)</p>
          <div className="grid grid-cols-2 gap-3 text-xs font-mono">
            <div className="p-3 bg-[#08080A] rounded-sm border border-[#26262B]"><span className="text-[#8A8A93]">Spot</span><div className="text-white text-lg">${r.spot?.toFixed(2)}</div></div>
            <div className="p-3 bg-[#08080A] rounded-sm border border-[#26262B]">
              <label className="text-[#8A8A93]">Put Strike (Kp)</label>
              <input type="number" step="0.5" value={kp} onChange={e => setKp(e.target.value)} data-testid="enter-trade-kp" className="w-full bg-transparent text-white text-lg font-mono border-0 outline-none p-0 mt-0.5" />
            </div>
            <div className="p-3 bg-[#08080A] rounded-sm border border-[#26262B]">
              <label className="text-[#8A8A93]">Premium (Pp)</label>
              <input type="number" step="0.01" value={pp} onChange={e => setPp(e.target.value)} data-testid="enter-trade-pp" className="w-full bg-transparent text-[#00FF88] text-lg font-mono border-0 outline-none p-0 mt-0.5" />
            </div>
            <div className="p-3 bg-[#08080A] rounded-sm border border-[#26262B]"><span className="text-[#8A8A93]">ROI CSP</span><div className={`text-lg ${parseFloat(roiCsp) >= 3 ? "text-[#00FF88]" : "text-[#FFB800]"}`}>{roiCsp}%</div></div>
          </div>
          {sc && (kpNum !== sc.csp_strike || ppNum !== sc.csp_credit) && (
            <div className="text-[10px] font-mono text-[#FFB800] flex items-center gap-2"><AlertTriangle size={12} /> Custom strike — differs from winner scenario (K=${sc.csp_strike} Pp=${sc.csp_credit?.toFixed(2)})</div>
          )}
          <textarea value={comment} onChange={e => setComment(e.target.value)} placeholder="Trade notes..." rows={2} className="w-full bg-[#08080A] border border-[#26262B] focus:border-[#00FF88] focus:outline-none text-white rounded-sm px-3 py-2 text-sm font-mono" />
          <button onClick={handleSave} disabled={saving || !kpNum || !ppNum} className="w-full bg-[#00FF88] text-[#08080A] font-bold py-3 rounded-sm hover:bg-[#33FF9F] text-sm disabled:opacity-40">{saving ? "Saving..." : "Open CSP Cycle"}</button>
        </div>
      </div>
    </div>
  );
}

// ==================== DASHBOARD / WES TRADES / SCANNER / SETTINGS / WHEEL TRACKER ====================
// (All previous page components stay the same - importing from the original)

/** Compact widget that breaks down WHY trades were skipped over the last N days.
 *  Answers: "is my ROI gate too tight? is broker liquidity the bottleneck? or are setups just not strong enough?"
 *  Only renders when the user has at least one NO_TRADE snapshot in the window — otherwise stays out of the way. */
export function SkipReasonsWidget() {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [windowDays, setWindowDays] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios.get(`${API}/forge/skip-reasons?days=${windowDays}`)
      .then(r => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [windowDays]);

  if (loading) return null;
  if (!data || data.no_trade_count === 0) return null;

  const reasons = data.reasons || [];
  // Color the dominant reason. We use amber to signal "caution — review", not an error.
  const barColor = "bg-[#FFB800]";

  return (
    <div
      data-testid="skip-reasons-widget"
      className="mb-5 border border-[#26262B] bg-[#121216] rounded-sm px-4 py-3"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <ShieldAlert size={14} className="text-[#FFB800]" />
          <h3 className="text-[11px] font-bold uppercase tracking-[0.18em] text-white">
            {t("skip.title")}
          </h3>
          <span data-testid="skip-reasons-meta" className="text-[10px] font-mono text-[#8A8A93]">
            · {data.no_trade_count} {t("skip.skipped_of")} {data.total_snapshots} ·{" "}
            <span className="text-[#FFB800]">{data.skip_rate_pct}% {t("skip.skip_rate")}</span>
          </span>
        </div>
        <div className="flex items-center gap-1">
          {[7, 30, 90].map(d => (
            <button
              key={d}
              data-testid={`skip-window-${d}`}
              onClick={() => setWindowDays(d)}
              className={`text-[10px] font-mono px-2 py-0.5 rounded-sm border ${
                windowDays === d ? "border-[#00FF88] text-[#00FF88] bg-[#00FF88]/10"
                                 : "border-[#26262B] text-[#8A8A93] hover:text-white"
              }`}
            >{d}d</button>
          ))}
        </div>
      </div>
      <div className="space-y-1.5">
        {reasons.map((r) => (
          <div key={r.reason} data-testid={`skip-reason-${r.reason}`} className="flex items-center gap-2 text-[11px]">
            <span className="w-[40px] text-right font-mono text-[#E4E4E7] font-bold">{r.pct}%</span>
            <div className="flex-1 h-2 bg-[#1A1A20] rounded-sm overflow-hidden">
              <div
                className={`h-full ${barColor} transition-all`}
                style={{ width: `${Math.max(2, r.pct)}%` }}
              />
            </div>
            <span className="w-[70px] font-mono text-[10px] text-[#8A8A93] tabular-nums">
              {r.count} {r.count === 1 ? t("skip.skip") : t("skip.skips")}
            </span>
            <span className="flex-[2] text-[#E4E4E7] truncate" title={`${t(`skip.reason.${r.reason}`, r.label) || r.label}: ${r.symbols.join(", ")}`}>
              <span className="text-[#FFB800]">{t(`skip.reason.${r.reason}`, r.label) || r.label}</span>
              {r.symbols.length > 0 && (
                <span className="text-[#5F5F66] font-mono ml-1.5 text-[10px]">
                  {r.symbols.slice(0, 4).join(" · ")}
                  {r.symbols.length > 4 ? " +" + (r.symbols.length - 4) : ""}
                </span>
              )}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}


// Iter113 — `/trades` retired; `WESTradesScan` Live-Scan tab
// removed per operator request (redundant with the Dashboard).
// The Decision Journal — the only unique surface that used to
// live under `/trades` — is now exposed at `/journal` as a
// standalone page.  The strict "high-conviction" quality filter
// (WES-P ≥7 AND WES-C ≥7 AND income ≥3%) was folded into the
// Dashboard as a toggle chip.


// Iter38 — Strike Decision Journal viewer. Sortable table of snapshots
// with status badge, candidate strike, ROI gate, freshness, plain English
// preview, and outcome placeholders. RBAC: admin sees all; viewer sees own.
function DecisionJournalPage() {
  const [rows, setRows] = useState([]);
  // Iter117.6 — Track journal load time so the shared FreshnessIndicator
  // can colour-code staleness and re-poll on demand.
  const [fetchedAt, setFetchedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState({ symbol: "", status: "" });
  const [expanded, setExpanded] = useState(null);
  // Iter116 — Manual Outcome Review modal state.
  const [reviewModal, setReviewModal] = useState(null);  // { decision, existing? }
  // Iter116.2 — "Copy summary" affordance per row. Single state field
  // holds the id of the most recently copied row so the button can flip
  // to a 1.8s "COPIED" confirmation state without re-renders elsewhere.
  const [copiedId, setCopiedId] = useState(null);
  // Iter116.3 — "Copy all visible rows" bulk export. Joins every visible
  // (filter-applied) row into one multi-line clipboard payload so the
  // operator can paste a full Journal snapshot into a notebook /
  // Discord during weekend review without exporting CSV.
  const [copiedAll, setCopiedAll] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const params = {};
      if (filter.symbol) params.symbol = filter.symbol.toUpperCase();
      if (filter.status) params.status = filter.status.toUpperCase();
      const r = await axios.get(`${API}/forge/journal/decisions`, { params });
      setRows(r.data?.rows || []);
      setFetchedAt(Date.now());
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this journal row? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/forge/journal/decisions/${id}`);
      setRows(rs => rs.filter(r => r.id !== id));
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    }
  };

  // Iter116.2 — Build a Trade-Verdict-style one-liner from a journal row
  // so the operator can paste prior decisions into chat / notes the same
  // way they copy the live verdict from Market Forge.
  const buildJournalLine = (r) => {
    const parts = [];
    parts.push(r.symbol || "—");
    if (r.status) parts.push(r.status);
    if (r.candidate_strike != null) parts.push(`@ $${Number(r.candidate_strike).toFixed(2)}`);
    const roiVal = r.roi_gate?.actual_bid;
    if (roiVal != null) {
      const passed = !!r.roi_gate?.passed;
      parts.push(`ROI ${(roiVal * 100).toFixed(2)}%${passed ? " ✓" : " ✕"}`);
    }
    if (r.gex_alignment?.dealer_regime) {
      parts.push(`GEX ${r.gex_alignment.dealer_regime.replace(/_/g, " ")}`);
    }
    if (r.expiration) parts.push(`exp ${r.expiration}`);
    if (r.created_at) parts.push(`@ ${new Date(r.created_at).toLocaleString()}`);
    return parts.join(" · ");
  };
  const onCopy = async (r) => {
    const line = buildJournalLine(r);
    try {
      await navigator.clipboard.writeText(line);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = line;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } catch { /* swallow */ }
      document.body.removeChild(ta);
    }
    setCopiedId(r.id);
    window.setTimeout(() => setCopiedId(c => (c === r.id ? null : c)), 1800);
  };

  // Iter116.3 — Bulk export current view. Builds a multi-line payload
  // with a small header line ("Decision Journal Export — N rows — TS"),
  // then one `buildJournalLine` row per visible journal entry. Reuses
  // the same one-liner format used by the per-row Copy button so a
  // paste into chat / notes looks identical whether the operator
  // copied a single row or the whole filtered view.
  const onCopyAll = async () => {
    if (rows.length === 0) return;
    const ts = new Date().toLocaleString();
    const header = `Decision Journal Export — ${rows.length} row${rows.length === 1 ? "" : "s"} — ${ts}`;
    const lines = [header, ""].concat(rows.map(r => `• ${buildJournalLine(r)}`));
    const payload = lines.join("\n");
    try {
      await navigator.clipboard.writeText(payload);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = payload;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } catch { /* swallow */ }
      document.body.removeChild(ta);
    }
    setCopiedAll(true);
    window.setTimeout(() => setCopiedAll(false), 1800);
  };

  // Iter116.4 — CSV export of the current filtered view. Streamed via
  // a Blob + temporary anchor so it stays 100 % client-side (no extra
  // backend endpoint). Columns picked for spreadsheet ergonomics —
  // every field that matters for pivot / filter analysis.
  const csvEscape = (val) => {
    if (val === null || val === undefined) return "";
    const s = String(val);
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const buildJournalCsv = (data) => {
    const cols = [
      "created_at", "symbol", "strategy", "status",
      "candidate_strike", "expiration", "decision_score",
      "roi_bid_pct", "roi_passed", "min_income_pct",
      "dealer_regime", "nearest_gex_wall",
      "wes_p", "wes_c", "nearest_wes_level",
      "pin_risk", "assignment_magnet_risk", "path_quality",
      "outcome", "user_email", "id",
    ];
    const header = cols.join(",");
    const rowsCsv = data.map(r => {
      const roiVal = r.roi_gate?.actual_bid;
      const roiPct = roiVal != null ? (roiVal * 100).toFixed(2) : "";
      return [
        r.created_at || "",
        r.symbol || "",
        r.strategy || "",
        r.status || "",
        r.candidate_strike != null ? Number(r.candidate_strike).toFixed(2) : "",
        r.expiration || "",
        r.decision_score ?? "",
        roiPct,
        r.roi_gate?.passed === true ? "TRUE" : r.roi_gate?.passed === false ? "FALSE" : "",
        r.roi_gate?.min_income_pct ?? "",
        r.gex_alignment?.dealer_regime || "",
        r.gex_alignment?.nearest_gex_wall != null ? Number(r.gex_alignment.nearest_gex_wall).toFixed(2) : "",
        r.wes_alignment?.wes_p ?? "",
        r.wes_alignment?.wes_c ?? "",
        r.wes_alignment?.nearest_wes_level || "",
        r.gex_alignment?.pin_risk || "",
        r.gex_alignment?.assignment_magnet_risk || "",
        r.gex_alignment?.path_quality || "",
        r.outcome_review?.outcome || "",
        r.user_email || "",
        r.id || "",
      ].map(csvEscape).join(",");
    });
    return [header].concat(rowsCsv).join("\n");
  };
  const onDownloadCsv = () => {
    if (rows.length === 0) return;
    const csv = buildJournalCsv(rows);
    // BOM so Excel opens UTF-8 CSV correctly.
    const blob = new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const today = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `decision-journal-${today}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.setTimeout(() => URL.revokeObjectURL(url), 1500);
  };

  const STATUS_STYLES = {
    ACCEPT:             "bg-[#00FF88]/15 text-[#00FF88] border-[#00FF88]/40",
    CAUTION:            "bg-[#FFB800]/15 text-[#FFB800] border-[#FFB800]/40",
    CAUTION_DATA_STALE: "bg-[#FFB800]/15 text-[#FFB800] border-[#FFB800]/40",
    REJECT:             "bg-[#FF3366]/15 text-[#FF3366] border-[#FF3366]/40",
    BLOCKED:            "bg-[#FF3366]/15 text-[#FF3366] border-[#FF3366]/40",
    NO_TRADE_ROI_FAIL:  "bg-[#FF3366]/15 text-[#FF3366] border-[#FF3366]/40",
  };

  return (
    <div data-testid="forge-decision-journal">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h2 className="text-3xl font-black tracking-tighter text-white font-['Chivo']">Decision Journal</h2>
          <p className="text-sm text-[#8A8A93] mt-1">Audit trail of every Strike Decision verdict you saved.</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Iter116.3 — Bulk-copy the current filtered view. */}
          <button
            type="button"
            data-testid="forge-decision-journal-copy-all"
            onClick={onCopyAll}
            disabled={rows.length === 0 || loading}
            title="Copy all visible rows as a multi-line summary"
            className={`inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.15em] px-3 py-2 rounded-sm border transition-colors ${
              copiedAll
                ? "bg-[#00FF88]/10 text-[#00FF88] border-[#00FF88]/40"
                : "bg-[#0A0A0E] text-[#8A8A93] border-[#26262B] hover:border-[#00FF88]/40 hover:text-[#00FF88] disabled:opacity-30 disabled:cursor-not-allowed"
            }`}
          >
            {copiedAll ? <Check size={12} /> : <Copy size={12} />}
            <span data-testid="forge-decision-journal-copy-all-label">
              {copiedAll ? "Copied" : `Copy all${rows.length > 0 ? ` (${rows.length})` : ""}`}
            </span>
          </button>
          {/* Iter116.4 — CSV download of the current filtered view. */}
          <button
            type="button"
            data-testid="forge-decision-journal-download-csv"
            onClick={onDownloadCsv}
            disabled={rows.length === 0 || loading}
            title="Download all visible rows as CSV (Excel-friendly)"
            className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.15em] px-3 py-2 rounded-sm border bg-[#0A0A0E] text-[#8A8A93] border-[#26262B] hover:border-[#FFB800]/40 hover:text-[#FFB800] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <Download size={12} />
            CSV
          </button>
          {/* Iter117.6 — Journal freshness chip (last load timestamp) +
              Refresh, via shared <FreshnessIndicator />. The previous
              "Refresh" button is now superseded by this widget. */}
          <FreshnessIndicator
            fetchedAt={fetchedAt}
            onRefresh={load}
            loading={loading}
            testidPrefix="forge-decision-journal"
            refreshTitle="Re-load decision journal rows"
          />
        </div>
      </div>

      <div className="flex items-center gap-2 mb-4">
        <input
          type="text"
          data-testid="forge-decision-journal-filter-symbol"
          value={filter.symbol}
          onChange={e => setFilter(f => ({ ...f, symbol: e.target.value.slice(0, 12) }))}
          placeholder="Symbol filter (e.g. SPY)"
          className="text-[11px] font-mono bg-[#0A0A0E] border border-[#26262B] rounded-sm px-2 py-1.5 text-white placeholder-[#5F5F66] focus:outline-none focus:border-[#FFB800]/40 w-40"
        />
        <select
          data-testid="forge-decision-journal-filter-status"
          value={filter.status}
          onChange={e => setFilter(f => ({ ...f, status: e.target.value }))}
          className="text-[11px] font-mono bg-[#0A0A0E] border border-[#26262B] rounded-sm px-2 py-1.5 text-white focus:outline-none focus:border-[#FFB800]/40"
        >
          <option value="">All statuses</option>
          <option value="ACCEPT">ACCEPT</option>
          <option value="CAUTION">CAUTION</option>
          <option value="CAUTION_DATA_STALE">CAUTION_DATA_STALE</option>
          <option value="REJECT">REJECT</option>
          <option value="BLOCKED">BLOCKED</option>
          <option value="NO_TRADE_ROI_FAIL">NO_TRADE_ROI_FAIL</option>
        </select>
        <span className="text-[10px] font-mono text-[#5F5F66] ml-auto">
          {loading ? "Loading…" : `${rows.length} row${rows.length === 1 ? "" : "s"}`}
        </span>
      </div>

      {err && (
        <InlineError
          testId="forge-decision-journal-error"
          className="mb-3"
          onRetry={load}
          message={err}
        />
      )}

      {!loading && rows.length === 0 && !err && (
        <div data-testid="forge-decision-journal-empty"
             className="bg-[#121216] border border-[#26262B] rounded-sm p-8 text-center">
          <BookOpen size={32} className="text-[#5F5F66] mx-auto mb-3" />
          <p className="text-[#8A8A93] text-sm">No decisions saved yet. Open Market Forge, evaluate a strike, and click Save Decision.</p>
        </div>
      )}

      {rows.length > 0 && (
        <div className="bg-[#121216] border border-[#26262B] rounded-sm overflow-hidden">
          <table className="w-full text-[11px] font-mono">
            <thead className="bg-[#0A0A0E] border-b border-[#26262B]">
              <tr className="text-[9px] font-bold uppercase tracking-[0.12em] text-[#5F5F66]">
                <th className="px-3 py-2 text-left">When</th>
                <th className="px-3 py-2 text-left">Symbol</th>
                <th className="px-3 py-2 text-left">Strategy</th>
                <th className="px-3 py-2 text-right">Strike</th>
                <th className="px-3 py-2 text-left">Exp</th>
                <th className="px-3 py-2 text-center">Status</th>
                <th className="px-3 py-2 text-right">Score</th>
                <th className="px-3 py-2 text-center">ROI</th>
                <th className="px-3 py-2 text-center">Outcome</th>
                <th className="px-3 py-2 text-left">User</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const cls = STATUS_STYLES[r.status] || "bg-[#5F5F66]/10 text-[#5F5F66] border-[#5F5F66]/30";
                const isOpen = expanded === r.id;
                const ts = r.created_at ? new Date(r.created_at).toLocaleString() : "—";
                // Iter38-fix: ROI column never silently shows "—". Prefer
                // actual_bid → actual_mid → "Not captured" (muted gray).
                // Color reflects roi_gate.passed when a value is present.
                const roiVal = r.roi_gate?.actual_bid != null
                  ? r.roi_gate.actual_bid
                  : (r.roi_gate?.actual_mid != null ? r.roi_gate.actual_mid : null);
                const roiPassed = r.roi_gate?.passed === true;
                const roiText = roiVal != null
                  ? `${(roiVal * 100).toFixed(2)}%${roiPassed ? " ✓" : " ✕"}`
                  : "Not captured";
                const roiClass = roiVal == null
                  ? "text-[#5F5F66] italic"
                  : (roiPassed ? "text-[#00FF88] font-bold" : "text-[#FF3366] font-bold");
                return (
                  <React.Fragment key={r.id}>
                    <tr data-testid={`forge-decision-journal-row-${r.id}`}
                        className="border-b border-[#26262B] hover:bg-[#0A0A0E]">
                      <td className="px-3 py-2 text-[#A0A0A8]">{ts}</td>
                      <td className="px-3 py-2 text-white font-bold">{r.symbol}</td>
                      <td className="px-3 py-2 text-[#A0A0A8]">{r.strategy?.replace("WHEEL_ITM_", "")}</td>
                      <td className="px-3 py-2 text-right text-white">
                        {r.candidate_strike != null ? `$${Number(r.candidate_strike).toFixed(2)}` : "—"}
                      </td>
                      <td className="px-3 py-2 text-[#A0A0A8]">{r.expiration || "—"}</td>
                      <td className="px-3 py-2 text-center">
                        <span className={`text-[9px] font-bold uppercase tracking-[0.1em] px-1.5 py-0.5 rounded-sm border ${cls}`}>
                          {r.status}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right text-white">{r.decision_score}</td>
                      <td className="px-3 py-2 text-center">
                        <span data-testid={`forge-decision-journal-roi-${r.id}`}
                              className={roiClass}
                              title={roiVal == null ? "ROI not captured at save time (broker chain did not include this strike or bid was unavailable)" : `ROI ${roiPassed ? "passed" : "failed"} the 3% gate`}>
                          {roiText}
                        </span>
                      </td>
                      {/* Iter39 — Outcome badge */}
                      <td className="px-3 py-2 text-center">
                        <OutcomeBadge review={r.outcome_review} rowId={r.id} />
                      </td>
                      <td className="px-3 py-2 text-[#5F5F66]">{r.user_email || "—"}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <button
                          type="button"
                          data-testid={`forge-decision-journal-toggle-${r.id}`}
                          onClick={() => setExpanded(isOpen ? null : r.id)}
                          className="text-[9px] uppercase tracking-[0.12em] text-[#8A8A93] hover:text-[#FFB800]"
                        >{isOpen ? "Hide" : "View"}</button>
                        <button
                          type="button"
                          data-testid={`forge-decision-journal-review-${r.id}`}
                          onClick={() => setReviewModal({ decision: r })}
                          className="ml-2 text-[9px] uppercase tracking-[0.12em] text-[#8A8A93] hover:text-[#FFB800]"
                        >{r.outcome_review ? "Edit Outcome" : "Review Outcome"}</button>
                        {/* Iter116.2 — Copy one-liner summary to clipboard. */}
                        <button
                          type="button"
                          data-testid={`forge-decision-journal-copy-${r.id}`}
                          onClick={() => onCopy(r)}
                          title="Copy summary to clipboard"
                          className={`ml-2 inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.12em] transition-colors ${
                            copiedId === r.id
                              ? "text-[#00FF88]"
                              : "text-[#5F5F66] hover:text-[#00FF88]"
                          }`}
                        >
                          {copiedId === r.id ? <Check size={10} /> : <Copy size={10} />}
                          {copiedId === r.id ? "Copied" : "Copy"}
                        </button>
                        {/* Delete row — Iter116.2 promoted from "Del" text to
                            an explicit trash-icon button so the operator
                            never confuses it with the View/Hide toggle. */}
                        <button
                          type="button"
                          data-testid={`forge-decision-journal-delete-${r.id}`}
                          onClick={() => onDelete(r.id)}
                          title="Delete this decision (cannot be undone)"
                          className="ml-2 inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.12em] text-[#5F5F66] hover:text-[#FF3366]"
                        >
                          <Trash2 size={10} />
                          Delete
                        </button>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-[#08080A] border-b border-[#26262B]">
                        <td colSpan={11} className="px-4 py-3 text-[10px] font-mono text-[#A0A0A8]">
                          <div className="grid grid-cols-3 gap-4">
                            <div>
                              <div className="text-[9px] font-bold uppercase tracking-[0.12em] text-[#5F5F66] mb-1">WES Alignment</div>
                              <div className="text-white">{r.wes_alignment?.status} · WES-P {r.wes_alignment?.wes_p?.toFixed?.(1)} / WES-C {r.wes_alignment?.wes_c?.toFixed?.(1)}</div>
                              <div className="text-[#8A8A93]">Nearest: {r.wes_alignment?.nearest_wes_level}</div>
                              {r.wes_alignment?.structure_read && (
                                <div className="text-[#8A8A93] mt-1 leading-relaxed">{r.wes_alignment.structure_read}</div>
                              )}
                            </div>
                            <div>
                              <div className="text-[9px] font-bold uppercase tracking-[0.12em] text-[#5F5F66] mb-1">GEX Alignment</div>
                              <div className="text-white">{r.gex_alignment?.status} · {r.gex_alignment?.dealer_regime?.replace(/_/g, " ")}</div>
                              <div className="text-[#8A8A93]">Wall ${Number(r.gex_alignment?.nearest_gex_wall || 0).toFixed(2)} · Pin {r.gex_alignment?.pin_risk} / Magnet {r.gex_alignment?.assignment_magnet_risk}</div>
                              <div className="text-[#8A8A93]">Path: {r.gex_alignment?.path_quality}</div>
                            </div>
                            <div>
                              <div className="text-[9px] font-bold uppercase tracking-[0.12em] text-[#5F5F66] mb-1">Outcome Tracking</div>
                              <div className="text-[#8A8A93]">T+1: {r.outcome_tracking?.t_plus_1 ?? "—"}</div>
                              <div className="text-[#8A8A93]">T+3: {r.outcome_tracking?.t_plus_3 ?? "—"}</div>
                              <div className="text-[#8A8A93]">T+7: {r.outcome_tracking?.t_plus_7 ?? "—"}</div>
                              <div className="text-[#8A8A93]">Expiration: {r.outcome_tracking?.expiration_outcome ?? "—"}</div>
                              <div className="text-[#8A8A93]">Cycle closed: {r.outcome_tracking?.cycle_closed ? "yes" : "no"}</div>
                            </div>
                          </div>
                          {r.plain_english && (
                            <div className="mt-3 text-[10px] font-mono text-[#A0A0A8] leading-relaxed bg-[#0A0A0E] border border-[#26262B] rounded-sm px-2 py-1.5">
                              {r.plain_english}
                            </div>
                          )}
                          {r.outcome_tracking?.notes && (
                            <div className="mt-2 text-[10px] font-mono text-[#FFB800] bg-[#FFB800]/5 border border-[#FFB800]/30 rounded-sm px-2 py-1.5">
                              Note: {r.outcome_tracking.notes}
                            </div>
                          )}
                          <div className="mt-2 flex flex-wrap gap-1">
                            {(r.risk_flags || []).map((f, i) => (
                              <span key={i} className="text-[8px] font-bold uppercase tracking-[0.12em] px-1.5 py-0.5 rounded-sm border bg-[#FF3366]/10 text-[#FF3366] border-[#FF3366]/30">{f}</span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Iter39 — Outcome Review modal */}
      {reviewModal && (
        <OutcomeReviewModal
          decision={reviewModal.decision}
          onClose={() => setReviewModal(null)}
          onSaved={() => { setReviewModal(null); load(); }}
        />
      )}
    </div>
  );
}

// Iter39 — Compact outcome badge for the journal table.
const OUTCOME_BADGE_STYLES = {
  WIN:                   "bg-[#00FF88]/15 text-[#00FF88] border-[#00FF88]/40",
  LOSS:                  "bg-[#FF3366]/15 text-[#FF3366] border-[#FF3366]/40",
  BREAK_EVEN:            "bg-[#8A8A93]/15 text-[#A0A0A8] border-[#5F5F66]/40",
  NOT_TRADED:            "bg-[#5F5F66]/15 text-[#8A8A93] border-[#5F5F66]/40",
  CANCELED:              "bg-[#5F5F66]/15 text-[#8A8A93] border-[#5F5F66]/40",
  OPEN:                  "bg-[#3BB5FF]/15 text-[#3BB5FF] border-[#3BB5FF]/40",
  ASSIGNED_ACCEPTED:     "bg-[#FFB800]/15 text-[#FFB800] border-[#FFB800]/40",
  ASSIGNED_NOT_ACCEPTED: "bg-[#FF3366]/15 text-[#FF3366] border-[#FF3366]/40",
  ROLLED:                "bg-[#3BB5FF]/15 text-[#3BB5FF] border-[#3BB5FF]/40",
};

function OutcomeBadge({ review, rowId }) {
  if (!review || !review.outcome) {
    return (
      <span data-testid={`forge-decision-journal-outcome-${rowId}`}
            className="text-[9px] font-mono text-[#5F5F66] italic">—</span>
    );
  }
  const cls = OUTCOME_BADGE_STYLES[review.outcome] || OUTCOME_BADGE_STYLES.BREAK_EVEN;
  const roi = review.actual_roi_pct;
  return (
    <span data-testid={`forge-decision-journal-outcome-${rowId}`}
          title={`Last edited ${review.updated_at || "—"} · rev ${review.revision_count || 1}`}
          className={`inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.1em] px-1.5 py-0.5 rounded-sm border ${cls}`}>
      {review.outcome.replace(/_/g, " ")}
      {roi != null && (
        <span className="font-mono normal-case">· {Number(roi).toFixed(2)}%</span>
      )}
      {review.revision_count > 1 && (
        <span data-testid={`forge-decision-journal-outcome-rev-${rowId}`}
              className="font-mono normal-case opacity-60">· r{review.revision_count}</span>
      )}
    </span>
  );
}

// Iter39 — Manual Outcome Review modal. Lets the user record (and edit)
// the outcome of a saved decision. Does NOT mutate the original journal
// snapshot — writes to /api/forge/journal/decisions/{id}/outcome.
const OUTCOME_OPTIONS = [
  "WIN", "LOSS", "BREAK_EVEN", "NOT_TRADED", "CANCELED", "OPEN",
  "ASSIGNED_ACCEPTED", "ASSIGNED_NOT_ACCEPTED", "ROLLED",
];

function OutcomeReviewModal({ decision, onClose, onSaved }) {
  const [form, setForm] = useState({
    outcome: "", trade_taken: null,
    fill_price: "", fill_date: "",
    exit_price: "", exit_date: "",
    closed_at_expiration: null, actual_roi_pct: "",
    assigned: null, lessons_learned: "", tags: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [existing, setExisting] = useState(null);
  const [err, setErr] = useState(null);
  const [ok, setOk] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setErr(null);
      try {
        const r = await axios.get(`${API}/forge/journal/decisions/${decision.id}/outcome`);
        if (!alive) return;
        setExisting(r.data);
        setForm({
          outcome:              r.data.outcome || "",
          trade_taken:          r.data.trade_taken,
          fill_price:           r.data.fill_price ?? "",
          fill_date:            r.data.fill_date || "",
          exit_price:           r.data.exit_price ?? "",
          exit_date:            r.data.exit_date || "",
          closed_at_expiration: r.data.closed_at_expiration,
          actual_roi_pct:       r.data.actual_roi_pct ?? "",
          assigned:             r.data.assigned,
          lessons_learned:      r.data.lessons_learned || "",
          tags:                 (r.data.tags || []).join(", "),
        });
      } catch (e) {
        if (e?.response?.status !== 404 && alive) {
          setErr(e?.response?.data?.detail || e.message);
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [decision.id]);

  const handleSubmit = async () => {
    if (saving) return;
    if (!form.outcome) { setErr("outcome is required"); return; }
    if (form.trade_taken === null || form.trade_taken === undefined) {
      setErr("trade_taken is required"); return;
    }
    setSaving(true); setErr(null); setOk(null);
    try {
      const payload = {
        outcome: form.outcome, trade_taken: form.trade_taken,
        fill_price: form.fill_price === "" ? null : Number(form.fill_price),
        fill_date: form.fill_date || null,
        exit_price: form.exit_price === "" ? null : Number(form.exit_price),
        exit_date: form.exit_date || null,
        closed_at_expiration: form.closed_at_expiration,
        actual_roi_pct: form.actual_roi_pct === "" ? null : Number(form.actual_roi_pct),
        assigned: form.assigned,
        lessons_learned: form.lessons_learned || null,
        tags: form.tags
          ? form.tags.split(",").map(t => t.trim()).filter(Boolean)
          : [],
      };
      const r = await axios.post(
        `${API}/forge/journal/decisions/${decision.id}/outcome`, payload);
      setOk(r.data.created ? "Outcome saved." :
              `Outcome updated · revision ${r.data.row.revision_count}.`);
      setExisting(r.data.row);
      setTimeout(() => onSaved && onSaved(), 800);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="forge-decision-outcome-modal"
         className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6"
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-[#121216] border border-[#26262B] rounded-sm w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#26262B]">
          <div>
            <h3 className="text-sm font-bold uppercase tracking-[0.18em] text-white">
              Review Outcome · {decision.symbol} ${Number(decision.candidate_strike).toFixed(2)}
            </h3>
            <p className="text-[10px] font-mono text-[#5F5F66] mt-0.5">
              Snapshot {decision.status} · score {decision.decision_score} ·
              {existing ? ` revision ${existing.revision_count}` : " no review yet"}
            </p>
          </div>
          <button
            type="button"
            data-testid="forge-decision-outcome-modal-close"
            onClick={onClose}
            className="text-[#8A8A93] hover:text-white"
          ><X size={18} /></button>
        </div>

        <div className="p-5 space-y-4">
          {loading && (
            <div className="text-[10px] font-mono text-[#5F5F66] flex items-center gap-2">
              <Loader2 size={12} className="animate-spin" /> Loading existing review…
            </div>
          )}
          {!loading && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[9px] font-bold uppercase tracking-[0.15em] text-[#8A8A93] mb-1.5 block">
                    Outcome <span className="text-[#FF3366]">*</span>
                  </label>
                  <select
                    data-testid="forge-decision-outcome-select"
                    value={form.outcome}
                    onChange={e => setForm(f => ({ ...f, outcome: e.target.value }))}
                    className="w-full text-[11px] font-mono bg-[#08080A] border border-[#26262B] rounded-sm px-2 py-2 text-white focus:outline-none focus:border-[#FFB800]/40"
                  >
                    <option value="">— select —</option>
                    {OUTCOME_OPTIONS.map(o => <option key={o} value={o}>{o.replace(/_/g, " ")}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[9px] font-bold uppercase tracking-[0.15em] text-[#8A8A93] mb-1.5 block">
                    Trade Taken <span className="text-[#FF3366]">*</span>
                  </label>
                  <div className="flex gap-2">
                    {[{v: true, l: "Yes"}, {v: false, l: "No"}].map(opt => (
                      <button
                        key={String(opt.v)}
                        type="button"
                        data-testid={`forge-decision-outcome-trade-taken-${opt.v}`}
                        onClick={() => setForm(f => ({ ...f, trade_taken: opt.v }))}
                        className={`flex-1 text-[11px] font-bold uppercase px-3 py-2 rounded-sm border ${
                          form.trade_taken === opt.v
                            ? "bg-[#00FF88]/15 text-[#00FF88] border-[#00FF88]/40"
                            : "bg-[#08080A] text-[#8A8A93] border-[#26262B] hover:border-[#00FF88]/40"}`}
                      >{opt.l}</button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <NumberField label="Fill Price" testid="forge-decision-outcome-fill-price"
                             value={form.fill_price}
                             onChange={v => setForm(f => ({ ...f, fill_price: v }))} />
                <DateField label="Fill Date" testid="forge-decision-outcome-fill-date"
                            value={form.fill_date}
                            onChange={v => setForm(f => ({ ...f, fill_date: v }))} />
                <NumberField label="Exit Price" testid="forge-decision-outcome-exit-price"
                             value={form.exit_price}
                             onChange={v => setForm(f => ({ ...f, exit_price: v }))} />
                <DateField label="Exit Date" testid="forge-decision-outcome-exit-date"
                            value={form.exit_date}
                            onChange={v => setForm(f => ({ ...f, exit_date: v }))} />
                <NumberField label="Actual ROI %" testid="forge-decision-outcome-actual-roi"
                             value={form.actual_roi_pct}
                             onChange={v => setForm(f => ({ ...f, actual_roi_pct: v }))} />
                <TriField label="Closed at Expiration" testid="forge-decision-outcome-closed-expiration"
                          value={form.closed_at_expiration}
                          onChange={v => setForm(f => ({ ...f, closed_at_expiration: v }))} />
                <TriField label="Assigned" testid="forge-decision-outcome-assigned"
                          value={form.assigned}
                          onChange={v => setForm(f => ({ ...f, assigned: v }))} />
                <div>
                  <label className="text-[9px] font-bold uppercase tracking-[0.15em] text-[#8A8A93] mb-1.5 block">Tags (comma separated)</label>
                  <input
                    type="text"
                    data-testid="forge-decision-outcome-tags"
                    value={form.tags}
                    onChange={e => setForm(f => ({ ...f, tags: e.target.value }))}
                    placeholder="pin_hit, fragile_path, rolled_out"
                    className="w-full text-[11px] font-mono bg-[#08080A] border border-[#26262B] rounded-sm px-2 py-2 text-white placeholder-[#5F5F66] focus:outline-none focus:border-[#FFB800]/40"
                  />
                </div>
              </div>

              <div>
                <label className="text-[9px] font-bold uppercase tracking-[0.15em] text-[#8A8A93] mb-1.5 block">Lessons Learned</label>
                <textarea
                  data-testid="forge-decision-outcome-lessons"
                  value={form.lessons_learned}
                  onChange={e => setForm(f => ({ ...f, lessons_learned: e.target.value.slice(0, 2000) }))}
                  placeholder="What did this trade teach you?"
                  rows={3}
                  className="w-full text-[11px] font-mono bg-[#08080A] border border-[#26262B] rounded-sm px-2 py-2 text-white placeholder-[#5F5F66] focus:outline-none focus:border-[#FFB800]/40"
                />
              </div>

              {err && (
                <InlineError testId="forge-decision-outcome-error" message={err} />
              )}
              {ok && (
                <div data-testid="forge-decision-outcome-success"
                     className="text-[10px] font-mono text-[#00FF88] bg-[#00FF88]/10 border border-[#00FF88]/30 rounded-sm px-2 py-1.5">
                  {ok}
                </div>
              )}

              <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#26262B]">
                <button
                  type="button"
                  data-testid="forge-decision-outcome-cancel"
                  onClick={onClose}
                  className="text-[10px] font-bold uppercase tracking-[0.15em] px-3 py-1.5 rounded-sm border bg-[#0A0A0E] text-[#8A8A93] border-[#26262B] hover:border-[#5F5F66]"
                >Cancel</button>
                <button
                  type="button"
                  data-testid="forge-decision-outcome-save"
                  onClick={handleSubmit}
                  disabled={saving || !form.outcome || form.trade_taken === null}
                  className={`text-[10px] font-bold uppercase tracking-[0.15em] px-3 py-1.5 rounded-sm border ${
                    saving || !form.outcome || form.trade_taken === null
                      ? "bg-[#08080A] text-[#5F5F66] border-[#26262B] cursor-not-allowed"
                      : "bg-[#00FF88]/15 text-[#00FF88] border-[#00FF88]/40 hover:bg-[#00FF88]/25"}`}
                >{saving ? "Saving…" : (existing ? "Update Outcome" : "Save Outcome")}</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function NumberField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-[9px] font-bold uppercase tracking-[0.15em] text-[#8A8A93] mb-1.5 block">{label}</label>
      <input
        type="number"
        step="0.01"
        data-testid={testid}
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full text-[11px] font-mono bg-[#08080A] border border-[#26262B] rounded-sm px-2 py-2 text-white focus:outline-none focus:border-[#FFB800]/40"
      />
    </div>
  );
}

function DateField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-[9px] font-bold uppercase tracking-[0.15em] text-[#8A8A93] mb-1.5 block">{label}</label>
      <input
        type="date"
        data-testid={testid}
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full text-[11px] font-mono bg-[#08080A] border border-[#26262B] rounded-sm px-2 py-2 text-white focus:outline-none focus:border-[#FFB800]/40"
      />
    </div>
  );
}

function TriField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-[9px] font-bold uppercase tracking-[0.15em] text-[#8A8A93] mb-1.5 block">{label}</label>
      <div className="flex gap-1">
        {[{v: true, l: "Yes"}, {v: false, l: "No"}, {v: null, l: "—"}].map(o => (
          <button
            key={String(o.v)}
            type="button"
            data-testid={`${testid}-${o.v}`}
            onClick={() => onChange(o.v)}
            className={`flex-1 text-[10px] font-bold uppercase px-2 py-1.5 rounded-sm border ${
              value === o.v
                ? "bg-[#FFB800]/15 text-[#FFB800] border-[#FFB800]/40"
                : "bg-[#08080A] text-[#8A8A93] border-[#26262B] hover:border-[#FFB800]/40"}`}
          >{o.l}</button>
        ))}
      </div>
    </div>
  );
}

// Iter115 — Legacy inline `Scanner` component (route `/scanner`,
// sidebar id `nav_scanner`) removed.  The redundant manual-symbols
// page has been fully superseded by `MarketScannerPage`
// (`/market-scanner`), which already runs the full WES + ROI ranked
// universe sweep and feeds the Strategy Scanner V2 bridge.  The
// legacy `ScanLine` icon, `nav_scanner` + `scanner_*` i18n keys, and
// `Scanner` component code block have all been deleted in lock-step.
// Backend `/api/scan/*` endpoints stay (they're still used by
// admin scheduled scans + viewer scan history tests).  See
// `tests/test_iter115_legacy_scanner_removed.py` for the protection
// asserting the UI never resurrects.


// Iter114 — Old inline `WheelTracker` + `CycleCard` removed; replaced
// by the dedicated `/app/frontend/src/pages/CycleTracker.js` page.
// `AddCCModal` / `CCRow` / `RuleRow` stay here (exported above) and
// are imported by the new page.


export function AddCCModal({ cycleId, onClose, onSaved }) {
  const [kc, setKc] = useState(""); const [pc, setPc] = useState(""); const [dte, setDte] = useState("");
  const [saving, setSaving] = useState(false); const [result, setResult] = useState(null);
  const [autoCC, setAutoCC] = useState(null); const [loadingAuto, setLoadingAuto] = useState(false);
  const [tab, setTab] = useState("auto");
  useEffect(() => { findAutoCC(); }, []); // eslint-disable-line
  const findAutoCC = async () => { setLoadingAuto(true); try { const r = await axios.get(`${API}/cycles/${cycleId}/auto-cc`); setAutoCC(r.data); } catch {} setLoadingAuto(false); };
  const selectCandidate = (c) => { setKc(String(c.kc)); setPc(String(c.pc)); setDte(String(c.dte_call)); setTab("manual"); };
  const handleSave = async () => { if (!kc || !pc || !dte) return; setSaving(true); try { const resp = await axios.post(`${API}/cycles/${cycleId}/cc`, { kc: parseFloat(kc), pc: parseFloat(pc), dte_call: parseInt(dte) }); setResult(resp.data); onSaved(); } catch (e) { alert(e.response?.data?.detail || "Error"); } setSaving(false); };
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-[#121216] border border-[#26262B] rounded-sm w-[640px] max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#26262B]"><h3 className="text-lg font-bold text-white font-['Chivo']">Add Covered Call</h3><button onClick={onClose} className="text-[#8A8A93] hover:text-white"><X size={18} /></button></div>
        <div className="flex border-b border-[#26262B]"><button onClick={() => setTab("auto")} className={`flex-1 px-4 py-2.5 text-xs font-bold uppercase border-b-2 -mb-px ${tab === "auto" ? "border-[#00FF88] text-[#00FF88]" : "border-transparent text-[#8A8A93]"}`}>Auto CC Finder</button><button onClick={() => setTab("manual")} className={`flex-1 px-4 py-2.5 text-xs font-bold uppercase border-b-2 -mb-px ${tab === "manual" ? "border-[#00FF88] text-[#00FF88]" : "border-transparent text-[#8A8A93]"}`}>Manual Entry</button></div>
        <div className="p-5">
          {tab === "auto" && <div className="space-y-4">
            {loadingAuto ? <div className="text-center py-8"><Loader2 size={24} className="animate-spin text-[#00FF88] mx-auto mb-2" /><p className="text-sm text-[#8A8A93]">Scanning chain...</p></div> : autoCC ? <>
              <div className="flex items-center justify-between text-xs font-mono text-[#8A8A93]"><span>{autoCC.symbol} EXP {autoCC.expiration} ({autoCC.dte_call}d) | Kp=${autoCC.kp} P_prev=${autoCC.p_prev}</span><button onClick={findAutoCC} className="text-[#007AFF]"><RefreshCw size={12} /></button></div>
              {autoCC.best_candidate && <div className="p-4 border-2 border-[#00FF88]/40 bg-[#00FF88]/[0.04] rounded-sm"><div className="flex items-center justify-between mb-3"><span className="text-xs font-bold uppercase text-[#00FF88]">Best (Highest Kc)</span><button onClick={() => selectCandidate(autoCC.best_candidate)} className="text-xs px-3 py-1.5 bg-[#00FF88] text-[#08080A] font-bold rounded-sm">Select</button></div><CCRow c={autoCC.best_candidate} /></div>}
              {autoCC.approved?.length > 1 && <div><p className="text-xs font-bold uppercase text-[#8A8A93] mb-2">All Approved ({autoCC.approved_count})</p><div className="space-y-1 max-h-[200px] overflow-y-auto">{autoCC.approved.map((c, i) => <CCRow key={i} c={c} onSelect={() => selectCandidate(c)} />)}</div></div>}
              {autoCC.approved_count === 0 && <div className="p-4 border border-[#FF3366]/20 bg-[#FF3366]/5 rounded-sm text-center"><XCircle size={20} className="text-[#FF3366] mx-auto mb-2" /><p className="text-sm text-[#FF3366]">No CC passes all rules</p></div>}
              {autoCC.near_threshold?.length > 0 && <div><p className="text-xs font-bold uppercase text-[#FFB800] mb-2">Near Threshold</p><div className="space-y-1">{autoCC.near_threshold.map((c, i) => <CCRow key={i} c={c} onSelect={() => selectCandidate(c)} />)}</div></div>}
            </> : <p className="text-sm text-[#8A8A93] text-center py-4">Could not load</p>}
          </div>}
          {tab === "manual" && <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3"><div><label className="text-xs font-bold uppercase text-[#8A8A93] mb-2 block">Kc</label><input type="number" step="0.5" value={kc} onChange={e => setKc(e.target.value)} className="w-full bg-[#08080A] border border-[#26262B] text-white rounded-sm px-3 py-2.5 text-sm font-mono" /></div><div><label className="text-xs font-bold uppercase text-[#8A8A93] mb-2 block">Pc</label><input type="number" step="0.01" value={pc} onChange={e => setPc(e.target.value)} className="w-full bg-[#08080A] border border-[#26262B] text-white rounded-sm px-3 py-2.5 text-sm font-mono" /></div><div><label className="text-xs font-bold uppercase text-[#8A8A93] mb-2 block">DTE</label><input type="number" value={dte} onChange={e => setDte(e.target.value)} className="w-full bg-[#08080A] border border-[#26262B] text-white rounded-sm px-3 py-2.5 text-sm font-mono" /></div></div>
            {result?.cc_rules && <div className="space-y-1 text-xs font-mono"><RuleRow label="DTE" value={`${result.cc_rules.dte_total}d/20d`} pass={result.cc_rules.dte_pass} /><RuleRow label="P/L" value={`$${result.cc_rules.pl_cycle?.toFixed(2)}>$0`} pass={result.cc_rules.pl_pass} /><RuleRow label="ROI" value={`${result.cc_rules.roi_cycle}%>=3%`} pass={result.cc_rules.roi_pass} /><RuleRow label="Quality" value={`$${result.cc_rules.pl_cycle?.toFixed(2)}>=$0.50`} pass={result.cc_rules.quality_pass} /></div>}
            <button onClick={result ? onClose : handleSave} disabled={saving || (!kc || !pc || !dte)} className="w-full bg-[#00FF88] text-[#08080A] font-bold py-3 rounded-sm text-sm disabled:opacity-40">{result ? "Done" : saving ? "Validating..." : "Add CC Leg"}</button>
          </div>}
        </div>
      </div>
    </div>
  );
}

export function CCRow({ c, onSelect }) {
  return (<div className="flex items-center justify-between px-3 py-2 rounded-sm text-xs font-mono bg-[#08080A] hover:bg-[#1A1A20]"><div className="flex items-center gap-4"><span className="text-white font-bold">Kc=${c.kc}</span><span className="text-[#00FF88]">${c.pc?.toFixed(2)}</span><span className="text-[#8A8A93]">{c.bid?.toFixed(2)}/{c.ask?.toFixed(2)}</span>{c.delta != null && <span className="text-[#8A8A93]">{"\u0394"}{c.delta}</span>}<span className="text-[#8A8A93]">OI:{c.oi}</span></div><div className="flex items-center gap-3"><span className={c.rule_pl ? "text-[#00FF88]" : "text-[#FF3366]"}>P/L ${c.pl_cycle?.toFixed(2)}</span><span className={c.rule_roi ? "text-[#00FF88]" : "text-[#FF3366]"}>ROI {c.roi_cycle}%</span>{onSelect && <button onClick={onSelect} className="text-[10px] px-2 py-1 border border-[#3F3F47] text-white hover:border-[#00FF88] rounded-sm">Select</button>}</div></div>);
}

export function RuleRow({ label, value, pass }) {
  return (<div className="flex items-center gap-2">{pass ? <CheckCircle2 size={12} className="text-[#00FF88]" /> : <XCircle size={12} className="text-[#FF3366]" />}<span className={pass ? "text-[#00FF88]" : "text-[#FF3366]"}>{label}: {value}</span></div>);
}



// ==================== LEADERBOARD (admin-only) ====================
// ==================== APP ====================
function App() {
  return (
    <BrowserRouter>
      <I18nProvider>
        <AuthProvider>
          <AppRoutes />
          {/* Iter119.18 — Global toast surface for transient feedback */}
          <Toaster position="top-right" richColors expand visibleToasts={4} />
        </AuthProvider>
      </I18nProvider>
    </BrowserRouter>
  );
}

function AppRoutes() {
  const { user, needsSetup } = useAuth();

  // Handle Emergent OAuth callback (URL fragment contains session_id) — must run before auth gate
  if (typeof window !== "undefined" && window.location.hash && window.location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }

  // First-run setup wizard (only if no admin exists in DB and we haven't seeded one)
  if (!user && needsSetup) {
    return (
      <Routes>
        <Route path="*" element={<SetupAdminPage />} />
      </Routes>
    );
  }

  // Strict auth gate: not logged in → login screen
  if (!user) {
    return (
      <Routes>
        <Route path="*" element={<LoginPage />} />
      </Routes>
    );
  }

  return (
    <MarketSnapshotProvider>
    <CFMInventoryProvider>
    <WESContextProvider>
    <MarketScannerBridgeProvider>
    <AuthedShell user={user} />
    </MarketScannerBridgeProvider>
    </WESContextProvider>
    </CFMInventoryProvider>
    </MarketSnapshotProvider>
  );
}

// Phase 7E — Authed shell holds the mobile drawer state and renders
// the responsive layout (sidebar + mobile topbar + main).  Lives
// inside the Router so `useLocation` is available for auto-close
// on route change.
function AuthedShell({ user }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  // Close the off-canvas drawer whenever the route changes so the
  // operator never lands on the next page with the menu still over
  // the content.
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  return (
    <div className="min-h-screen bg-[#08080A]">
      <Sidebar mobileOpen={mobileOpen} onClose={() => setMobileOpen(false)} />
      <div className="flex flex-col min-h-screen md:ml-[220px]">
        <MobileTopBar onOpen={() => setMobileOpen(true)} />
        <main className="flex-1 p-4 md:p-6 lg:p-8">
          <AuthedRoutes user={user} />
        </main>
      </div>
    </div>
  );
}

// Iter116.7 — Authed routes wrapped in a page-level error boundary keyed
// to the current pathname so a runtime crash on one page never leaves the
// operator staring at a blank screen, and navigating away auto-recovers.
// Iter116.7 — Dev-only crash trigger used to smoke-test PageErrorBoundary.
// Throws synchronously during render so the boundary's getDerivedStateFromError
// + componentDidCatch hooks fire. Hidden from the sidebar; reachable only by
// typing /dev/crashtest in the URL.
function CrashTest() {
  throw new Error("CrashTest: intentional error to verify PageErrorBoundary works");
}

function AuthedRoutes({ user }) {
  const location = useLocation();
  return (
    <LiveStreamProvider>
      <PageErrorBoundary resetKey={location.pathname}>
        <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/strategy-scanner" element={<StrategyScannerV2 />} />
        <Route path="/journal" element={<DecisionJournalPage />} />
        <Route path="/log" element={<CycleTrackerPage />} />
        <Route path="/market-scanner" element={<MarketScannerPage />} />
        <Route path="/forge" element={<MarketForge />} />
        <Route path="/performance" element={<Performance />} />
        <Route path="/leaderboard" element={user.role === "admin" ? <Leaderboard /> : <Navigate to="/" replace />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/shadow" element={<ShadowMode />} />
        <Route path="/dev/tools" element={<DeveloperTools />} />
        {/* Iter116.7 — dev-only route that intentionally throws so the
            error boundary can be smoke-tested. Safe to keep in prod: it's
            hidden from the sidebar and only renders when navigated to
            explicitly. */}
        <Route path="/dev/crashtest" element={<CrashTest />} />
        <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </PageErrorBoundary>
    </LiveStreamProvider>
  );
}

export default App;
